import { X } from 'lucide-react';
import { CSSProperties, Fragment, ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { fetchAlertExpectations, fetchDaytradeBackdrop, fetchDaytradeChart, fetchDaytradeFlow, fetchDaytradeNews, fetchDaytradeOrderbook, fetchDaytradeScan, fetchDaytradeSeries, fetchEntryTiming, fetchRelayWatch, fetchSniper, fetchStockSensitivity } from '../api';
import { AlertExpectations, DaytradeBackdrop, DaytradeBar, DaytradeFlowItem, DaytradeItem, DaytradeNews, DaytradeOrderbook, DaytradeScan, DaytradeSeries, EntryTiming, RelayWatch, SniperResponse, StockSensitivity } from '../types';
import { isOwner } from '../owner';
import { addPulse, disablePush, DtAlert, enablePush, getAlerts, getPushPref, isMarketAlert, isPushEnabled, isSoundEnabled, isWatched, playChime, PulsePoint, PUSH_CAT_META, setLicenseCtx, setPushPref, setSoundEnabled, splitAlert, syncPins, toggleWatch, useAlerts, usePulse, useRides, useWatch, WATCH_MAX } from '../alerts';
import StockSearch from './daytrade/StockSearch';
// 🎨 단타 화면 배치 리스킨(B안 대담형, 07-22) — 스코프 스타일(키프레임·hover·3열 그리드·모션축소).
//   ⚠️ Pretendard(자체 호스팅) import는 08-23 전역 승격으로 index.tsx로 옮겼다 — 이제 전 화면이 같은 글꼴을 쓴다.
import './daytrade/dc.css';
// 🚪 v3 전면 개편(단타탭 제로) — 옛 조종 화면/줍줍/종목표/수급카드 렌더 제거. 컴포넌트 파일은 보존(TicketPage·CockpitCard·AlertFeed·PaperReport·MinuteChart·OrderBook·SelfCheckStrip은 다른 곳/승격 대비 유지).


export function fmtEok(v: number): string {
  const a = Math.abs(v);
  const s = v > 0 ? '+' : v < 0 ? '−' : '';
  if (a >= 10000) return `${s}${(a / 10000).toFixed(1)}조`;
  if (a >= 100) return `${s}${Math.round(a).toLocaleString('ko-KR')}억`;
  if (a >= 1) return `${s}${a.toFixed(0)}억`;
  return '0';
}

// 🎨 v4 피드 아이템(07-21 재설계) — 카테고리 아이콘 4종 · 방향 색 틴트 · 중요도 위계.
const _PIN_KINDS = new Set(['stop', 'target', 'breakdown', 'trail', 'timestop', 'overnight', 'pricealert']);
// 카테고리 아이콘 4종만: 🏦 큰손 / ⏩ 큰손 매수 시작 / 📌 핀 / 👁 감시(그 외 감시 채널 통과분)
export function alertCat(a: DtAlert): string {
  if (a.kind === 'flow_buy' || a.kind === 'flow_sell') return '🏦';
  if (a.kind === 'flowearly' || a.kind === 'flowearly_confirmed') return '⏩';   // ⏩ 큰손 매수 시작 — 08-04부터 화면은 90초 확인
  // 🌅 아침 판정(08-04) — 장 시작 전 하루치 판정. 자체 카테고리가 없으면 마지막 👁(감시)로 오분류돼 범례가 거짓말이 된다.
  if (a.kind.startsWith('morning_') || a.kind === 'expiry_kosdaq') return '🌅';
  if (a.kind === 'night_rebound') return '🌙';   // 🌙 급락일 밤 반등(08-20) — 아침 판정과 같은 '하루 단위 판정'이지만 시각(마감 직전)과 지평(밤 하나)이 달라 독립 칸. 안 넣으면 마지막 👁(감시)로 오분류돼 범례가 거짓말이 된다.
  if (a.kind === 'prime') return '🎖️';   // 🎖️ 종합 신호 자리(07-28 승격) — 여러 근거가 겹친 순간만. 급변 ⚡와 성격이 달라 독립 칸(없으면 마지막 👁 감시로 오분류).
  if (a.kind === 'marketmove' || a.kind === 'stockmove' || a.kind === 'knee') return '⚡';   // ⚡ 급변 계열 — 지수(marketmove)·하이닉스 힘 감지(knee, 07-24 승격)·가격 경보(stockmove, 강등·잔류). 서버 푸시(push.py _EV_ICON)와 아이콘 통일. 없으면 마지막 👁(감시)로 오분류.
  if (a.kind === 'news') return '🗞️';
  if (a.kind === 'lifeline') return '🔌';   // 🔌 데이터 끊김·복구(07-25) — 자체 카테고리. 안 넣으면 마지막 👁(감시)로 오분류된다.
  if (a.kind === 'disclosure_warning') return '📄';   // 📄 공시 경고(08-12) — 내 종목의 공시. 안 넣으면 마지막 👁(감시)로 오분류돼 범례가 거짓말이 된다.
  if (a.kind === 'flowreact') return '🐋';   // 🐋 큰손 매수 뒤 하이닉스 반응(08-15 임시 합격) — 큰손 수급이 재료라 🏦와 이웃이지만 '반응 확인' 성격이라 독립 칸(없으면 마지막 👁 감시로 오분류).
  if (_PIN_KINDS.has(a.kind)) return '📌';
  return '👁';
}
// 방향 틴트 — 매수/상승 +1(빨강) · 매도/하락 −1(파랑) · 핀 도구/불명 0(회색).
export function alertDir(a: DtAlert): 1 | -1 | 0 {
  if (_PIN_KINDS.has(a.kind)) return 0;                 // 핀·가격알림 = 중립 회색(과제 명시)
  if (a.kind === 'flow_buy' || a.fdir === 'buy') return 1;
  if (a.kind === 'flow_sell' || a.fdir === 'sell') return -1;
  if (typeof a.dir === 'number' && a.dir !== 0) return a.dir > 0 ? 1 : -1;
  return 0;
}
// 중요도 — 큰손·큰손 매수 시작·지수/하이닉스 급변·하이닉스 힘 감지는 주연(크게), 핀·감시는 조연(표준).
//   marketmove(지수 급락)·knee(하이닉스 힘 감지, 07-24 승격) 포함 — 레버리지·인버스 사용자의 주력 신호라 주연 크기. stockmove는 강등됐으나 잔류(무해).
//   🦵 knee는 '흐름 카드 한 장'이 제자리 갱신되는 구조(07-24 실사용 3차) — cont 갱신본도 같은 카드라 크기 유지(주연). cont는 소리·검증 중 라우팅용.
//   🔌 lifeline(07-25)도 주연 — 드물게 뜨지만 "지금 알림을 믿어도 되나"가 걸린 경보라 놓치면 안 된다.
//   🌅 아침 판정(08-04)도 주연 — 하루에 한 번, 조건이 맞는 날에만 나오는 '오늘 어떤 날인가' 판정이라 놓치면 안 된다.
//   ⏩ 90초 확인은 확인 전 원본 flowearly의 화면 알림을 승계했으므로 같은 크기(확인 전 원본은 검증 중이라 화면에 안 나온다).
//   📄 공시 경고(08-12)도 주연 — 드물게(내 종목에 그 공시가 뜰 때만) 나오지만 "가진 걸 정리할지"가 걸린 경고라 놓치면 안 된다.
//   🐋 큰손 매수 뒤 하이닉스 반응(08-15 임시 합격)도 주연 — 레버리지·인버스 매매의 주력인 '큰손 → 반도체' 방향 신호다.
//   🌙 급락일 밤 반등(08-20)도 주연 — 하루에 많아야 한 번, 그것도 급락한 날에만 나오고 "밤을 넘길까"가 걸린 판정이다.
export const isMajorAlert = (a: DtAlert): boolean => a.kind === 'flow_buy' || a.kind === 'flow_sell' || a.kind === 'flowearly' || a.kind === 'flowearly_confirmed' || a.kind === 'marketmove' || a.kind === 'stockmove' || a.kind === 'knee' || a.kind === 'prime' || a.kind === 'lifeline' || a.kind.startsWith('morning_') || a.kind === 'expiry_kosdaq' || a.kind === 'disclosure_warning' || a.kind === 'flowreact' || a.kind === 'night_rebound';

// 단타 설정 영속화 — localStorage 읽기/쓰기(JSON). 다른 탭 갔다 와도(컴포넌트 remount) 그대로 유지.
function lsGet<T>(key: string, fallback: T): T {
  try { const v = localStorage.getItem(key); return v === null ? fallback : (JSON.parse(v) as T); } catch { return fallback; }
}
function lsSet(key: string, v: unknown) { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* noop */ } }

// 실시간 틱 플래시 — value가 바뀌면 그 칸을 잠깐 빛낸다(상승=빨강/하락=파랑). 자동 폴링 중 '살아있음'을 시각화.
export function FlashNum({ value, className = '', children }: { value: number; className?: string; children: ReactNode }) {
  const prev = useRef(value);
  const idRef = useRef(0);
  const [flash, setFlash] = useState<{ cls: string; id: number } | null>(null);
  useEffect(() => {
    if (value !== prev.current) {
      const cls = value > prev.current ? 'tick-up' : 'tick-down';
      prev.current = value;
      idRef.current += 1;
      setFlash({ cls, id: idRef.current });   // key 변경 → 애니메이션 재시작
    }
  }, [value]);
  // key가 바뀌면 remount되어 0.8s 플래시 애니메이션이 처음부터 재생됨
  return <span key={flash?.id ?? 0} className={`inline-block rounded px-1 ${className} ${flash?.cls ?? ''}`}>{children}</span>;
}

// 📊 알림 기대 분포 모듈 캐시(StanceBadge 등 top-level 컴포넌트 공용). DayTradePage가 10분마다 갱신.





// 📈 궤적 스파크라인(07-21 화면 배치 세련화) — 오늘 주체별 누적/베이시스의 흐름을 큰손 카드 하단에.
//   사실만: 결측(null) 구간은 선을 끊는다(이어붙임=위조 금지). 색은 '가리키는' 것 — 채도 낮은 방향색(마지막 부호 기준,
//   양수 살구 #c97e7e·음수 블루 #7fa3d6·중립 회색). 카드 폭에 맞춰 가로로 늘어나되(preserveAspectRatio none) 선 두께는 고정.
function TrendSpark({ pts, dir, height = 22, fill = true, dot = false }: { pts?: (number | null)[] | null; dir?: number | null; height?: number; fill?: boolean; dot?: boolean }) {
  const gid = useId().replace(/[^a-zA-Z0-9]/g, '');   // 인스턴스별 그라데이션 id(한 화면 다수)
  if (!pts || pts.length < 2) return null;
  const vals = pts.filter((v): v is number => v != null);
  if (vals.length < 2) return null;
  const min = Math.min(...vals), max = Math.max(...vals), range = max - min;
  const W = 100, H = 26, n = pts.length, step = W / (n - 1);
  let d = '', pen = false;                       // 선: 결측(null)에서 끊음(이어붙임=위조 금지)
  const drawn: [number, number][] = [];          // 채움/끝점용 실측 점만
  pts.forEach((v, i) => {
    if (v == null) { pen = false; return; }
    const norm = range ? (v - min) / range : 0.5;              // 평평하면 중앙
    const x = i * step, y = H - 2 - norm * (H - 4);            // 상하 2px 여백
    d += `${pen ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)} `;
    pen = true; drawn.push([x, y]);
  });
  const sign = dir != null && dir !== 0 ? dir : vals[vals.length - 1];   // dir 우선, 없으면 마지막 값 부호
  const stroke = sign > 0 ? '#E06C5B' : sign < 0 ? '#5C8FD6' : '#5A6479';   // 방향색(디자인 웜/쿨)
  const [ex, ey] = drawn[drawn.length - 1];
  // 채움 영역 — 방향색 20%→0 세로 그라데이션(디자인). 결측은 이어 그려 채움만 매끈(선은 끊김 유지).
  const area = fill && drawn.length > 1
    ? `M${drawn[0][0].toFixed(1)},${H} ` + drawn.map(([x, y]) => `L${x.toFixed(1)},${y.toFixed(1)}`).join(' ') + ` L${ex.toFixed(1)},${H} Z`
    : '';
  return (
    <div style={{ position: 'relative', lineHeight: 0 }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height={height} className="block" aria-hidden>
        {area && (
          <>
            <defs>
              <linearGradient id={`dcg${gid}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor={stroke} stopOpacity="0.2" />
                <stop offset="1" stopColor={stroke} stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={area} fill={`url(#dcg${gid})`} stroke="none" />
          </>
        )}
        <path d={d.trim()} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      </svg>
      {dot && <span style={{ position: 'absolute', right: 0, top: `${(ey / H) * 100}%`, width: 4, height: 4, borderRadius: '50%', background: stroke, transform: 'translate(50%,-50%)' }} />}
    </div>
  );
}


// ══════════ 🎨 B안 대담형 리스킨 헬퍼(07-22) — 표현 계층 전용. 데이터·로직·문구 불변 ══════════
// 16×16 인라인 SVG 아이콘(currentColor) — design/B안 대담형.dc.html에서 path 그대로 추출.
const dcSvg = (size: number, inner: ReactNode, opts: { fill?: boolean; sw?: number } = {}) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill={opts.fill ? 'currentColor' : 'none'} stroke={opts.fill ? 'none' : 'currentColor'} strokeWidth={opts.sw ?? 1.6} strokeLinecap="round" strokeLinejoin="round">{inner}</svg>
);
const DcIcon = {
  bank: (s = 13) => dcSvg(s, <><path d="M2.5 5.8 8 2.6l5.5 3.2" /><path d="M4 8.2v3.6M8 8.2v3.6M12 8.2v3.6" /><path d="M2.6 13.4h10.8" /></>),
  pulse: (s = 13) => dcSvg(s, <path d="M1.6 8.2h2.6L5.8 4l3.2 8 1.6-3.8h3.8" />),
  chart: (s = 13) => dcSvg(s, <><path d="M2 13.4h12" /><path d="M3.2 10.4 6.6 7l2.6 2.6 3.8-4.4" /></>),
  calendar: (s = 13) => dcSvg(s, <><rect x="2.4" y="3.2" width="11.2" height="10.2" rx="1.6" /><path d="M2.4 6.6h11.2M5.4 1.8v2.8M10.6 1.8v2.8" /></>),
  news: (s = 13) => dcSvg(s, <><rect x="2.2" y="3.2" width="11.6" height="9.6" rx="1.6" /><path d="M4.8 6.2h6.4M4.8 8.4h6.4M4.8 10.6h3.6" /></>, { sw: 1.5 }),
  bolt: (s = 12) => dcSvg(s, <path d="M9.2 1.6 3.6 9.1h3.2l-.9 5.3 5.6-7.5H8.3l.9-5.3Z" />, { fill: true }),
  ff: (s = 13) => dcSvg(s, <><path d="M2.8 4.2v7.6L8 8 2.8 4.2Z" /><path d="M8.6 4.2v7.6L13.8 8 8.6 4.2Z" /></>, { fill: true }),
  pin: (s = 13) => dcSvg(s, <path d="M5.5 2.2h5M6.4 2.4l-.4 4.1c-1 .5-1.7 1.2-1.9 2.1h7c-.2-.9-.9-1.6-1.9-2.1l-.4-4.1M8 10.5V14" />, { sw: 1.5 }),
  eye: (s = 13) => dcSvg(s, <><path d="M1.7 8s2.3-3.9 6.3-3.9S14.3 8 14.3 8s-2.3 3.9-6.3 3.9S1.7 8 1.7 8Z" /><circle cx="8" cy="8" r="1.9" /></>, { sw: 1.5 }),
  info: (s = 12) => dcSvg(s, <><circle cx="8" cy="8" r="5.9" /><path d="M8 7.4v3.4M8 4.9v.3" /></>),
  bell: (s = 12, off = false) => dcSvg(s, <><path d="M12.2 11H3.8s1.2-1.3 1.2-4.2a3 3 0 0 1 6 0c0 2.9 1.2 4.2 1.2 4.2Z" /><path d="M6.9 13.2a1.15 1.15 0 0 0 2.2 0" />{off && <path d="M3.2 3.2l9.6 9.6" />}</>),
  speaker: (s = 12, off = false) => dcSvg(s, <><path d="M2.6 6.2v3.6h2.2L8 12.6V3.4L4.8 6.2H2.6Z" />{off ? <path d="M10.6 6.2 13.4 9M13.4 6.2 10.6 9" /> : <path d="M10.4 5.8a3.2 3.2 0 0 1 0 4.4" />}</>),
  eq: (s = 12) => dcSvg(s, <path d="M2.2 7v2M4.8 5.2v5.6M7.4 3.4v9.2M10 5.2v5.6M12.6 7v2" />),
  gear: (s = 12) => dcSvg(s, <><circle cx="8" cy="8" r="2.1" /><path d="M8 2.2v1.9M8 11.9v1.9M2.2 8h1.9M11.9 8h1.9M3.9 3.9l1.35 1.35M10.75 10.75l1.35 1.35M12.1 3.9l-1.35 1.35M5.25 10.75 3.9 12.1" /></>, { sw: 1.5 }),
  // 🌅 아침 판정(08-04) — 지평선 위로 뜬 해. 장 시작 전 하루 판정이라는 뜻을 그림 하나로.
  sun: (s = 13) => dcSvg(s, <><path d="M1.8 12.4h12.4" /><path d="M4.6 12.4a3.4 3.4 0 0 1 6.8 0" /><path d="M8 3.2v1.6M3.4 5.2l1.1 1.1M12.6 5.2l-1.1 1.1" /></>, { sw: 1.5 }),
  // 🌙 급락일 밤 반등(08-20) — 초승달. 마감 직전에 '오늘 밤'을 판정한다는 뜻을 그림 하나로.
  moon: (s = 13) => dcSvg(s, <path d="M12.6 9.6A5.4 5.4 0 0 1 6.4 3.4a5.4 5.4 0 1 0 6.2 6.2Z" />, { sw: 1.5 }),
};

// 금액 문자열에서 끝 단위(조/억/원) 분리 — 단위는 ~52% 크기·85% 불투명으로 렌더(디자인).
function splitNumUnit(s: string | null | undefined): { num: string; unit: string } {
  if (s == null) return { num: '—', unit: '' };
  const m = s.match(/^(.*?)(조|억|원)$/);
  return m ? { num: m[1], unit: m[2] } : { num: s, unit: '' };
}

// 알림 본문 — 문장 속 '금액/수치'만 방향색·굵게(주연 16.5px), 괄호부(오늘 …)는 흐리게(#7E8BA1).
//   ⚠️ 글자는 한 자도 안 바꾸고 substring만 <span>으로 감쌈(카피 불변 — 규칙 1).
const _DC_AMT = /([+\-−]?[0-9][0-9,.]*\s*(?:조|억|%p|%|원|주))/;
function renderBody(text: string, dir: 1 | -1 | 0, amtSize = 16.5): ReactNode {
  if (!text) return null;
  const dirColor = dir > 0 ? '#E06C5B' : dir < 0 ? '#5C8FD6' : '#B5C0D2';
  return text.split(/(\([^)]*\))/g).map((seg, i) => {
    if (/^\([^)]*\)$/.test(seg)) return <span key={i} style={{ color: '#7E8BA1' }}>{seg}</span>;
    return seg.split(new RegExp(_DC_AMT.source, 'g')).map((p, j) =>
      _DC_AMT.test(p)
        ? <span key={`${i}-${j}`} style={{ fontWeight: 750, fontSize: `${amtSize}px`, color: dirColor }}>{p}</span>
        : <span key={`${i}-${j}`}>{p}</span>
    );
  });
}
// 칩 속 수치만 방향색·650(디자인, 칩과 동일 크기). 글자 불변(substring만 감쌈).
function renderChipText(text: string, dir: 1 | -1 | 0): ReactNode {
  const dirColor = dir > 0 ? '#E06C5B' : dir < 0 ? '#5C8FD6' : '#8D99AE';
  return text.split(new RegExp(_DC_AMT.source, 'g')).map((p, j) =>
    _DC_AMT.test(p)
      ? <span key={j} style={{ fontWeight: 650, color: dirColor }}>{p}</span>
      : <span key={j}>{p}</span>
  );
}

// 알림의 '시(hour)' — 시간대 기준값(10시/9시)용. ts 우선, 없으면 시각 문자열 파싱.
function hourOf(a: DtAlert): number {
  if (typeof a.ts === 'number' && a.ts > 0) return new Date(a.ts).getHours();
  const c = (a.time || '').replace(/[^0-9:]/g, '');
  const h = parseInt(c.split(':')[0], 10);
  return Number.isFinite(h) ? h : -1;
}

// 카테고리별 피드 노드(24px 타일) — 디자인 위계·색. dir는 🏦 큰손 방향 틴트(매수 웜/매도 쿨).
function dcNode(cat: string, dir: 1 | -1 | 0): { bg: string; border: string; color: string; icon: ReactNode } {
  switch (cat) {
    case '🏦': return dir < 0
      ? { bg: '#0F1520', border: 'rgba(92,143,214,.4)', color: '#5C8FD6', icon: DcIcon.bank(13) }
      : { bg: '#1A1214', border: 'rgba(224,108,91,.4)', color: '#E06C5B', icon: DcIcon.bank(13) };
    case '⚡': return { bg: '#14121F', border: 'rgba(144,137,230,.4)', color: '#9089E6', icon: DcIcon.bolt(12) };
    case '⏩': return { bg: '#0E1A19', border: 'rgba(69,184,168,.4)', color: '#45B8A8', icon: DcIcon.ff(13) };
    case '📌': return { bg: '#1C1710', border: 'rgba(220,168,94,.4)', color: '#DCA85E', icon: DcIcon.pin(13) };
    case '🔌': return { bg: '#1E1409', border: 'rgba(224,121,60,.45)', color: '#E0793C', icon: DcIcon.pulse(13) };   // 🔌 데이터 끊김·복구 — 맥박선 아이콘(수신이 뛰고 있나)
    // 📄 공시 경고(08-12) — 서류 아이콘 + 하락 파랑(이 앱에서 파랑 = 내림·주의). 뉴스(#76839A 회색)와 색으로 갈린다.
    case '📄': return { bg: '#0F1520', border: 'rgba(92,143,214,.4)', color: '#5C8FD6', icon: DcIcon.news(13) };
    // 🐋 큰손 매수 뒤 하이닉스 반응(08-15 임시 합격) — '오르는 선' 아이콘(반응이 시작됐다) + 장미빛.
    //   🏦 큰손 매수(#E06C5B)와 이웃한 따뜻한 색이되 한눈에 갈리는 톤으로 뒀다(재료는 같은 큰손, 성격은 '반응 확인').
    case '🐋': return { bg: '#1B1218', border: 'rgba(217,138,158,.42)', color: '#D98A9E', icon: DcIcon.chart(13) };
    case '🎖️': return { bg: '#1C1608', border: 'rgba(224,184,76,.45)', color: '#E0B84C', icon: DcIcon.bolt(12) };   // 🎖️ 종합 신호 자리(07-28 승격) — 범례 금색(#E0B84C)과 같은 색. 빠지면 👁 감시 회색으로 떨어져 범례가 거짓말이 된다
    // 🌅 아침 판정(08-04) — 장 시작 전 판정이라 방향 색을 따르지 않고 '아침' 한 색으로(하루에 한 번 나오는 다른 종류의 말).
    case '🌅': return dir < 0
      ? { bg: '#141B26', border: 'rgba(92,143,214,.4)', color: '#5C8FD6', icon: DcIcon.sun(13) }
      : { bg: '#1B1610', border: 'rgba(226,170,88,.45)', color: '#E2AA58', icon: DcIcon.sun(13) };
    // 🌙 급락일 밤 반등(08-20) — 밤을 뜻하는 남보라 한 색(방향은 항상 매수 쪽이라 색을 가르지 않는다).
    //   🌅 아침(주황)과 한눈에 갈리고 ⚡급변 보라(#9089E6)보다 파랑 쪽으로 눕혀 이웃과도 구분된다.
    case '🌙': return { bg: '#131829', border: 'rgba(124,140,224,.45)', color: '#7C8CE0', icon: DcIcon.moon(13) };
    default:  return { bg: '#141A22', border: 'rgba(139,153,175,.4)', color: '#8B99AF', icon: DcIcon.eye(13) };   // 👁 감시
  }
}

// 피드 헤더 범례 6종(디자인) — 색점 + 라벨.
const DC_LEGEND: { label: string; dot: string }[] = [
  { label: '큰손', dot: 'linear-gradient(90deg,#E06C5B 50%,#5C8FD6 50%)' },
  { label: '매수 시작', dot: '#45B8A8' },
  { label: '큰손 반응', dot: '#D98A9E' },   // 🐋 큰손 매수 뒤 하이닉스 반응(08-15 임시 합격) — 큰손이 산 뒤 하이닉스가 실제로 따라 오른 순간
  { label: '종합 신호', dot: '#E0B84C' },   // 🎖️ 종합 신호(07-28 승격) — 여러 근거가 겹침. 급변과 색을 갈라 한눈에 구분
  { label: '급변', dot: '#9089E6' },   // ⚡ 지수(marketmove)·하이닉스 힘 감지(knee, 07-24 승격) — 같은 보라 노드
  { label: '아침', dot: 'linear-gradient(90deg,#E2AA58 50%,#5C8FD6 50%)' },   // 🌅 아침 판정(08-04) — 방향에 따라 주황(매수쪽)/파랑(주의쪽)
  { label: '밤 반등', dot: '#7C8CE0' },   // 🌙 급락일 밤 반등(08-20) — 마감 직전(15:20) 하루 단위 판정
  { label: '내 핀', dot: '#DCA85E' },
  { label: '감시', dot: '#8B99AF' },
  { label: '뉴스', dot: '#76839A' },
  { label: '공시', dot: '#5C8FD6' },   // 📄 공시 경고(08-12) — 내 종목의 전환사채·유상증자·자사주 처분 공시
  { label: '데이터', dot: '#E0793C' },   // 🔌 데이터 끊김·복구(07-25) — 시장 신호가 아니라 서버 수신 상태
];

// ── B안 재사용 스타일 상수(CSSProperties) ──
const DC_BTN: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, height: 29, padding: '0 11px', borderRadius: 8, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', flex: 'none', background: 'transparent', lineHeight: 1 };
const DC_GHOST: CSSProperties = { ...DC_BTN, border: '1px solid #202A3C', color: '#8E9AB0', fontWeight: 500 };
const DC_ON: CSSProperties = { ...DC_BTN, border: '1px solid rgba(220,168,94,.35)', background: 'rgba(220,168,94,.1)', color: '#DCA85E', fontWeight: 600 };
const DC_SECHEAD: CSSProperties = { display: 'flex', alignItems: 'center', gap: 7, color: '#525E75' };
const DC_SECLABEL: CSSProperties = { fontSize: 11.5, fontWeight: 700, letterSpacing: '.09em', color: '#6E7B90' };
const DC_HAIR: CSSProperties = { height: 1, background: '#141B28', margin: '16px -18px' };
const DC_CHIP: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, height: 22, padding: '0 8px', borderRadius: 6, border: '1px solid #1F2938', background: 'rgba(19,26,40,.6)', fontSize: 11.5, fontWeight: 500, color: '#8D99AE', whiteSpace: 'nowrap' };
const DC_LINKCHIP: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, height: 22, padding: '0 8px', borderRadius: 6, border: '1px solid rgba(220,168,94,.3)', background: 'rgba(220,168,94,.07)', fontSize: 11.5, fontWeight: 600, color: '#DCA85E', cursor: 'pointer', whiteSpace: 'nowrap' };

// 계획 입력 칸 — 모듈 레벨로 분리(컴포넌트 안에 정의하면 폴링 재렌더마다 remount돼 포커스가 날아감).
// 로컬 draft로 자유 입력하고 blur/Enter 때만 부모에 반영(타이핑 중엔 2초 폴링과 무관).
function PlanField({ label, value, cls, onCommit }: { label: string; value: number; cls: string; onCommit: (n: number) => void }) {
  const [draft, setDraft] = useState(String(value));
  const editing = useRef(false);
  // 외부에서 값이 바뀌고(다른 데서 수정) 편집 중이 아니면 동기화
  useEffect(() => { if (!editing.current) setDraft(String(value)); }, [value]);
  const commit = () => { editing.current = false; const n = Math.round(Number(draft)); if (Number.isFinite(n) && n > 0 && n !== value) onCommit(n); else setDraft(String(value)); };
  return (
    <label className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <span className="text-dc-ink3">{label}</span>
      <input type="number" value={draft}
        onFocus={() => { editing.current = true; }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className={`w-20 bg-dc-bg1 border border-[#202A3C] rounded px-1.5 py-0.5 font-mono text-right ${cls} focus:border-dc-point/50 outline-none`} />
    </label>
  );
}

// 포지션 사이징 입력칸(계좌·리스크%) — 모듈 레벨 분리. PositionSizer 안에 두면 2초 폴링 재렌더마다
// remount돼 포커스가 날아감(매수가 칸 PlanField와 동일한 버그·동일한 해법). 로컬 draft로 입력하고 blur/Enter 때만 반영.
function SizerField({ value, width, suffix, onCommit }: { value: number; width: string; suffix?: string; onCommit: (n: number) => void }) {
  const [draft, setDraft] = useState(String(value));
  const editing = useRef(false);
  useEffect(() => { if (!editing.current) setDraft(String(value)); }, [value]);
  const commit = () => { editing.current = false; const n = Number(draft); if (Number.isFinite(n) && n > 0) onCommit(n); else setDraft(String(value)); };
  return (
    <span className="inline-flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
      <input type="number" value={draft}
        onFocus={() => { editing.current = true; }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className={`${width} bg-dc-bg1 border border-[#202A3C] rounded px-1 py-0.5 font-mono text-right text-dc-ink1 focus:border-dc-point/50 outline-none`} />
      {suffix && <span className="text-dc-ink3">{suffix}</span>}
    </span>
  );
}

// 포지션 사이징 — 계좌·리스크%를 한 번 정해두면(브라우저 기억), 손절폭 기준 '권장 수량·투입금액'을 계산.
// 핵심: "한 번에 계좌의 X%만 잃는다"를 강제 → 손절만 지키면 큰 손실이 구조적으로 막힘.
function readNum(key: string, def: number): number {
  try { const v = Number(localStorage.getItem(key)); return Number.isFinite(v) && v > 0 ? v : def; } catch { return def; }
}
function PositionSizer({ entry, stop }: { entry: number; stop: number }) {
  const [acct, setAcct] = useState(() => readNum('dt_account', 0));   // 미입력이면 0 — 가짜 추천 안 띄움
  const [risk, setRisk] = useState(() => readNum('dt_risk', 1));
  const hasAcct = acct > 0;
  const setA = (n: number) => { setAcct(n); try { localStorage.setItem('dt_account', String(n)); } catch { /* noop */ } };
  const setR = (n: number) => { setRisk(n); try { localStorage.setItem('dt_risk', String(n)); } catch { /* noop */ } };
  const perShare = entry - stop;                       // 1주당 위험(매수가−손절)
  const ok = perShare > 0 && entry > 0;
  const riskWon = acct * (risk / 100);                 // 감당할 손실 한도(원)
  let qty = ok ? Math.floor(riskWon / perShare) : 0;
  const capByCash = ok ? Math.floor(acct / entry) : 0; // 현금으로 살 수 있는 최대
  const capped = ok && qty > capByCash;
  if (capped) qty = capByCash;
  const cost = qty * entry;
  return (
    <div className="pt-1.5 border-t border-dc-hair space-y-1">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-dc-ink3">
        <span>💰 계좌</span><SizerField value={acct} onCommit={setA} width="w-24" suffix="원" />
        <span>· 한 번 위험</span><SizerField value={risk} onCommit={setR} width="w-10" suffix="%" />
        <span className="text-dc-ink3">= 최대 손실 {Math.round(riskWon).toLocaleString('ko-KR')}원</span>
      </div>
      {ok ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-dc-ink2">권장 수량 <b className="font-mono text-dc-point text-[13px]">{qty.toLocaleString('ko-KR')}주</b></span>
          <span className="text-dc-ink3">투입 <b className="font-mono text-dc-ink2">{cost.toLocaleString('ko-KR')}원</b> <span className="text-dc-ink3">({hasAcct ? Math.round((cost / acct) * 100) : 0}%)</span></span>
          {!hasAcct && <span className="text-dc-ink3">계좌를 입력하면 손절폭 기준 권장 수량이 나와요</span>}
          {hasAcct && capped && <span className="text-dc-warn">현금 한도로 캡(손절 너무 멂)</span>}
          {hasAcct && qty === 0 && <span className="text-dc-warn">손절폭 대비 위험금액 부족 — 리스크% 올리거나 손절 좁히기</span>}
        </div>
      ) : (
        <p className="text-dc-ink3">손절이 매수가보다 높거나 값이 없어 계산 불가</p>
      )}
    </div>
  );
}

// 핀(보유) 종목 진입 플랜 — 매수가·손절·목표를 '고정'하고 직접 수정. 현재가 대비 손익·도달 거리 표시.
function PinnedPlanEditor({ it, plan, onChange }: { it: DaytradeItem; plan: PinPlan; onChange: (p: PinPlan) => void }) {
  const pnl = ((it.price / plan.entry - 1) * 100);
  const toTarget = ((plan.target / it.price - 1) * 100);
  const toStop = ((plan.stop / it.price - 1) * 100);
  const risk = plan.entry - plan.stop, reward = plan.target - plan.entry;
  const rr = risk > 0 ? (reward / risk) : null;
  return (
    <div className="text-[11.5px] space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {/* ❹ 보유/관찰 토글 — 관찰(미보유)이면 손절/목표 알림 없이 진입 신호만 받음. */}
        <button onClick={() => onChange({ ...plan, owned: plan.owned === false })}
          title={plan.owned === false ? '👀 관찰 중(미보유) — 누르면 보유로. 관찰이면 손절/목표 알림 억제·진입 신호만.' : '💼 보유 중 — 누르면 관찰(미보유)로. 지켜보기만 할 때(보유 알림 억제).'}
          className={`font-semibold px-1.5 py-0.5 rounded border ${plan.owned === false ? 'border-dc-down/45 text-dc-down bg-dc-down/[0.08]' : 'border-dc-point/40 text-dc-point'}`}>
          {plan.owned === false ? '👀 관찰' : '💼 보유'}
        </button>
        <span className="text-dc-ink2">현재가 <b className="font-mono text-dc-ink1">{it.price.toLocaleString('ko-KR')}</b></span>
        {plan.owned === false
          ? <span className="text-dc-ink3">미보유 · 계획만 잡아두고 지켜보는 중</span>
          : <span className={pnl >= 0 ? 'text-dc-up' : 'text-dc-down'}>매수가 대비 <b className="font-mono">{pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}%</b></span>}
        {rr && <span className="text-dc-ink3">손익비 1:{rr.toFixed(1)}</span>}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <PlanField label="매수가" value={plan.entry} cls="text-dc-ink1" onCommit={(n) => onChange({ ...plan, entry: n })} />
        <PlanField label="손절" value={plan.stop} cls="text-dc-down" onCommit={(n) => onChange({ ...plan, stop: n })} />
        <PlanField label="목표" value={plan.target} cls="text-dc-up" onCommit={(n) => onChange({ ...plan, target: n })} />
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-dc-ink3">
        <span>목표까지 <b className={toTarget >= 0 ? 'text-dc-up' : 'text-dc-ink2'}>{toTarget >= 0 ? '+' : ''}{toTarget.toFixed(1)}%</b></span>
        <span>손절까지 <b className="text-dc-down">{toStop.toFixed(1)}%</b></span>
      </div>
      {/* 포지션 사이징 — 고정 매수가·손절 기준 권장 수량 */}
      {plan.stop < plan.entry && <PositionSizer entry={plan.entry} stop={plan.stop} />}
      <p className="text-dc-ink3 text-[10.5px] leading-snug">숫자 고치고 <b>Enter</b>나 칸 밖 클릭하면 반영(입력 중엔 자동갱신 안 건드림). 가격이 이 <b>고정값</b>에 닿으면 알림. 핀 해제하면 계획도 지워집니다.</p>
    </div>
  );
}


// (알림 타입·ALERT_KIND·감지·푸시는 ../alerts 의 항상 떠 있는 엔진으로 이전 — 메뉴 떠나도 동작)

// 핀(보유) 종목 계획 — 핀하는 순간 고정. 알림은 이 고정값으로 판단(현재가 따라 안 움직임).
type PinPlan = { entry: number; stop: number; target: number; owned?: boolean };   // ❹ owned=false=관찰(미보유)
// 핀할 때 기본 계획 — 매수가=현재가, 손절/목표는 그 순간의 자동값(없으면 -1.5% 손절·손익비 1:2).
function defaultPlan(it: DaytradeItem): PinPlan {
  const entry = it.price;
  const stop = it.stop_price && it.stop_price < entry ? it.stop_price : Math.round(entry * 0.985);
  const target = it.target_price && it.target_price > entry ? it.target_price : Math.round(entry + 2 * (entry - stop));
  return { entry, stop, target };
}







export default function DayTradePage() {
  const [data, setData] = useState<DaytradeScan | null>(null);
  const [, setLoading] = useState(false);
  // ↓ 툴바 설정 — localStorage에 저장해 다른 탭 갔다 와도 유지(아래 useEffect로 변경 시 기록). v3에서 토글 UI 철수라 setter는 미사용(값만 읽음).
  const [auto] = useState(() => lsGet('dt_auto', true));
  const [sel] = useState<{ ticker: string; name: string } | null>(null);
  const [, setBars] = useState<DaytradeBar[]>([]);
  const [, setChartLoading] = useState(false);
  const [tf] = useState(() => lsGet('dt_chart_tf', '1m'));   // 차트 시간프레임 1m|5m|30m|1d (유지)
  useEffect(() => { lsSet('dt_chart_tf', tf); }, [tf]);
  const [mode] = useState(() => lsGet('dt_mode', 'volume'));   // volume|amount|surge|plunge|vi
  const [exEtf] = useState(() => lsGet('dt_exetf', false));   // 인버스·레버리지 ETF 제외
  const [sortPb] = useState(() => lsGet('dt_sortpb', true)); // 눌림목순 정렬(지금 살 만함 맨 위) — 기본 ON
  const [onlyGood] = useState(() => lsGet('dt_onlygood', false)); // 지금 살 만함·관찰만 보기(트리아지)
  const [marketFilter] = useState(() => lsGet('dt_market', 'all')); // 전체|KOSPI|KOSDAQ
  // 위 설정이 바뀌면 저장 — setter 호출부를 안 건드려도 모든 변경이 기록됨
  useEffect(() => {
    lsSet('dt_auto', auto); lsSet('dt_mode', mode); lsSet('dt_exetf', exEtf);
    lsSet('dt_sortpb', sortPb); lsSet('dt_onlygood', onlyGood); lsSet('dt_market', marketFilter);
  }, [auto, mode, exEtf, sortPb, onlyGood, marketFilter]);
  // 고정 종목(매수한 종목 추이 추적) — 티커 목록 + 이름. localStorage로 새로고침에도 유지.
  const [pinned, setPinned] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('dt_pinned') || '[]'); } catch { return []; }
  });
  const [pinNames, setPinNames] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem('dt_pin_names') || '{}'); } catch { return {}; }
  });
  // 핀 종목별 고정 계획(매수가·손절·목표). 핀할 때 현재값으로 잡고 직접 수정 가능.
  const [pinPlans, setPinPlans] = useState<Record<string, PinPlan>>(() => {
    try { return JSON.parse(localStorage.getItem('dt_pin_plans') || '{}'); } catch { return {}; }
  });
  const savePinPlans = (next: Record<string, PinPlan>) => {
    setPinPlans(next);
    try { localStorage.setItem('dt_pin_plans', JSON.stringify(next)); } catch { /* noop */ }
  };
  const updatePlan = (ticker: string, plan: PinPlan) => savePinPlans({ ...pinPlans, [ticker]: plan });
  const togglePin = (it: DaytradeItem) => {
    const adding = !pinned.includes(it.ticker);
    setPinned((prev) => {
      const next = adding ? [it.ticker, ...prev].slice(0, 8) : prev.filter((t) => t !== it.ticker);
      try { localStorage.setItem('dt_pinned', JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
    setPinNames((prev) => {
      const next = { ...prev, [it.ticker]: it.name };
      try { localStorage.setItem('dt_pin_names', JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
    // 핀 추가 시 계획을 현재값으로 고정, 해제 시 계획 삭제.
    const nextPlans = { ...pinPlans };
    if (adding) nextPlans[it.ticker] = defaultPlan(it);
    else delete nextPlans[it.ticker];
    savePinPlans(nextPlans);
  };
  // 🔎 검색에서 핀 토글 — 스캔에 없는 종목이라 시세가 없어 계획은 안 잡고 티커·이름만 등록.
  // (뒤의 effect가 스캔에 실제로 잡히는 순간 기본 계획을 채워줌.) 이미 핀돼 있으면 해제.
  const pinTicker = (ticker: string, name: string) => {
    if (pinned.includes(ticker)) {
      const it = allItems.find((i) => i.ticker === ticker);
      if (it) { togglePin(it); return; }
      // 아직 스캔에 안 잡힌 핀 해제 — 목록/이름/계획에서 직접 제거
      setPinned((prev) => { const next = prev.filter((t) => t !== ticker); try { localStorage.setItem('dt_pinned', JSON.stringify(next)); } catch { /* noop */ } return next; });
      const nextPlans = { ...pinPlans }; delete nextPlans[ticker]; savePinPlans(nextPlans);
      return;
    }
    setPinned((prev) => { const next = [ticker, ...prev].slice(0, 8); try { localStorage.setItem('dt_pinned', JSON.stringify(next)); } catch { /* noop */ } return next; });
    setPinNames((prev) => { const next = { ...prev, [ticker]: name }; try { localStorage.setItem('dt_pin_names', JSON.stringify(next)); } catch { /* noop */ } return next; });
  };
  // 👁 감시 — 보유 핀과 별개로 '계속 지켜볼' 종목. 스캔에 항상 포함돼 그 종목의 모든 알림이 뜸(최대 8).
  const watch = useWatch();
  useRides();   // 🎯 '탔어요' 마크 변경 시 알림 피드 재렌더
  const [watchWarn, setWatchWarn] = useState('');
  const toggleWatchStock = (it: { ticker: string; name: string }) => {
    const ok = toggleWatch(it.ticker, it.name);
    if (!ok) { setWatchWarn(`감시는 최대 ${WATCH_MAX}개 — 다른 종목을 먼저 해제하세요`); setTimeout(() => setWatchWarn(''), 3000); }
  };
  const [, setOb] = useState<DaytradeOrderbook | null>(null);   // 선택 종목 호가창(폴링만)
  const [pushOn, setPushOn] = useState(isPushEnabled());   // 🔔 토글 UI(실상태는 alerts 스토어/localStorage)
  const [soundOn, setSoundOn] = useState(isSoundEnabled()); // 🔊 알림 사운드
  const [, setFlows] = useState<Record<string, DaytradeFlowItem>>({});  // 종목별 추정 수급(30초 폴링)
  const [backdrop, setBackdrop] = useState<DaytradeBackdrop | null>(null);  // ① 시장 배경(60초 폴링)
  const [series, setSeries] = useState<DaytradeSeries['series'] | null>(null);  // 📈 스파크라인 궤적(60초 폴링)
  const [news, setNews] = useState<DaytradeNews | null>(null);   // 🗞️ 상황판 2층(지금 뉴스 + 오늘의 시간표) — 5분 폴링
  const [sniper, setSniper] = useState<SniperResponse | null>(null);   // 🌙 밤새 보유 판단 배너용(관리자 전용 엔드포인트)
  const [bdAt, setBdAt] = useState(0);   // 🔄 마지막 수급 갱신 시각(데이터 신선도 기준점, 07-09)
  const [nowTs, setNowTs] = useState(() => Date.now());   // 🫀 핵심 게이트박동 줄 5초 틱(신선도 카운트 갱신용)
  const [, setExpData] = useState<AlertExpectations>({});   // 📊 알림 기대 분포(과거 실측) — 10분 폴링(localStorage로 alerts.ts에 전달)
  const [, setSens] = useState<StockSensitivity | null>(null);  // 📈 선택 종목 수급 민감도(폴링만)
  const [, setEntryTiming] = useState<EntryTiming | null>(null);  // #6 시간대 등급(폴링만)
  const [, setRelay] = useState<RelayWatch | null>(null);  // #15 상한가 릴레이(폴링만)
  // 📊 기대 분포 로드 + 모듈 캐시(StanceBadge 툴팁 공용). 10분 주기(자주 안 바뀜).
  useEffect(() => {
    // #10 성적 자동 볼륨 — expData를 localStorage로 넘겨 alerts.ts 사운드 로직이 승률 하위 종류를 자동 소거하게.
    const load = () => fetchAlertExpectations().then((e) => { setExpData(e); try { localStorage.setItem('dt_exp_cache', JSON.stringify(e)); } catch { /* noop */ } }).catch(() => {});
    load(); const t = setInterval(load, 10 * 60 * 1000); return () => clearInterval(t);
  }, []);
  // #6 시간대 등급 로드(10분) — 지금 시간대에 진입 신호가 실측상 약한지 판단용.
  useEffect(() => {
    const load = () => fetchEntryTiming().then(setEntryTiming).catch(() => {});
    load(); const t = setInterval(load, 10 * 60 * 1000); return () => clearInterval(t);
  }, []);
  // #15 상한가 릴레이 로드(30분) — 지난 거래일 급등 목록('오늘 주목').
  useEffect(() => {
    const load = () => fetchRelayWatch().then(setRelay).catch(() => {});
    load(); const t = setInterval(load, 30 * 60 * 1000); return () => clearInterval(t);
  }, []);
  // 📈 선택 종목 바뀌면 수급 민감도 조회(6h 서버 캐시라 부담 적음)
  useEffect(() => {
    setSens(null);
    if (sel?.ticker && /^\d{6}$/.test(sel.ticker)) fetchStockSensitivity(sel.ticker).then(setSens).catch(() => {});
  }, [sel?.ticker]);
  const flowTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  // 사용법 가이드 — 처음 본 사람에겐 펼쳐서, 한 번 닫으면 기억
  const [showGuide, setShowGuide] = useState(() => {
    try { return localStorage.getItem('dt_guide_seen') !== '1'; } catch { return true; }
  });
  const [, setAwayInfo] = useState<{ min: number; stock: number; market: number } | null>(null);   // 🅓 부재중 브리핑(폴링만)
  const hiddenAtRef = useRef(0);
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) hiddenAtRef.current = Date.now();
      else if (hiddenAtRef.current) {
        const min = Math.floor((Date.now() - hiddenAtRef.current) / 60000);
        if (min >= 3) {
          const recent = getAlerts().filter((a) => (a.ts ?? 0) >= hiddenAtRef.current);
          setAwayInfo({ min, stock: recent.filter((a) => !isMarketAlert(a.kind)).length, market: recent.filter((a) => isMarketAlert(a.kind)).length });
        }
        hiddenAtRef.current = 0;
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const obTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const chartTimer = useRef<ReturnType<typeof setInterval> | null>(null);   // 분봉 실시간 폴링
  const alerts = useAlerts();   // 공유 스토어(엔진이 항상 채움 — 메뉴 떠나도 유지)
  // 🔔 새 알림 반짝 — 사운드는 나는데 표시가 다른 칸/필터/스크롤에 가려 "이전 거로 보이는" 혼란 해소.
  // 알림 맨 위(id) 바뀌면 새로 들어온 것 → 카드 헤더에 몇 개·어느 칸(종목/시장)인지 6초 표시.
  const prevTopRef = useRef('');
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [, setAlertFlash] = useState<{ stock: number; market: number } | null>(null);
  const [hlIds, setHlIds] = useState<Set<string>>(new Set());   // 새 알림 10초 하이라이트
  useEffect(() => {
    const topId = alerts[0]?.id;
    if (topId && prevTopRef.current && topId !== prevTopRef.current) {
      const idx = alerts.findIndex((a) => a.id === prevTopRef.current);
      const news = idx === -1 ? alerts.slice(0, 8) : alerts.slice(0, idx);
      const stock = news.filter((a) => !isMarketAlert(a.kind)).length;
      const market = news.filter((a) => isMarketAlert(a.kind)).length;
      if (stock + market > 0) {
        setAlertFlash({ stock, market });
        if (flashTimer.current) clearTimeout(flashTimer.current);
        flashTimer.current = setTimeout(() => setAlertFlash(null), 6000);
        // 새로 들어온 알림 id를 10초간 하이라이트(이 배치 것만 10초 뒤 해제)
        const ids = news.map((a) => a.id);
        setHlIds((prev) => { const n = new Set(prev); ids.forEach((id) => n.add(id)); return n; });
        setTimeout(() => setHlIds((prev) => { const n = new Set(prev); ids.forEach((id) => n.delete(id)); return n; }), 10000);
      }
    }
    if (topId) prevTopRef.current = topId;
  }, [alerts]);
  const pulse = usePulse();     // 🫀 시장 펄스 시계열(엔진·이 탭이 함께 누적)
  // 📌 핀(보유) 손절/목표가 바뀌면 서버에 동기화 → 서버가 그 종목 도달 시 이 기기로 푸시(3단계)
  useEffect(() => { syncPins(); }, [pinned, pinPlans]);

  const dismissGuide = () => {
    try { localStorage.setItem('dt_guide_seen', '1'); } catch { /* noop */ }
    setShowGuide(false);
  };
  // 가이드는 '맨 처음 한 번'만 펼쳐 보여줌 — 마운트되는 즉시 본 것으로 기록해, 닫기를 안 눌러도
  // 다음 방문부턴 접힌 상태로 뜬다(상단 '사용법' 버튼으로 언제든 다시 펼침). 초기 펼침 여부는
  // 위 useState 초기화(이 effect보다 먼저 실행)가 이미 결정하므로, 여기서 기록해도 이번 표시엔 영향 없음.
  useEffect(() => {
    try { localStorage.setItem('dt_guide_seen', '1'); } catch { /* noop */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchDaytradeScan(mode, exEtf, pinned);   // 핀 종목은 유니버스 밖이어도 추적
      // 같은 모드에서 일시적 빈 응답이면 직전 정상 데이터 유지(연동 대기 깜빡임 방지)
      setData((prev) => (!r.kis_ready && prev?.kis_ready && (prev.items?.length ?? 0) > 0 && prev.mode === r.mode) ? prev : r);
    } catch { /* 네트워크 실패 — 직전 화면 유지 */ } finally { setLoading(false); }
  }, [mode, exEtf, pinned]);

  useEffect(() => { load(); }, [load]);   // 모드·ETF 바뀌면 즉시 재로딩

  const ready = data?.kis_ready ?? false;
  useEffect(() => {
    if (timer.current) clearInterval(timer.current);
    if (ready && auto) timer.current = setInterval(load, 2000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [ready, auto, load]);

  // 선택 종목 호가창 — 열려 있는 동안 1.5초 폴링
  useEffect(() => {
    if (obTimer.current) clearInterval(obTimer.current);
    if (!sel) { setOb(null); return; }
    const poll = () => fetchDaytradeOrderbook(sel.ticker).then(setOb).catch(() => {});
    poll();
    obTimer.current = setInterval(poll, 1500);
    return () => { if (obTimer.current) clearInterval(obTimer.current); };
  }, [sel]);

  // 선택 종목 차트 — 초기 1회(로딩 표시) + 폴링 갱신. 종목·시간프레임 바뀌면 재로딩.
  useEffect(() => {
    if (chartTimer.current) clearInterval(chartTimer.current);
    if (!sel) { setBars([]); return; }
    const load = (initial: boolean) => fetchDaytradeChart(sel.ticker, tf)
      .then((c) => setBars(c.bars))
      .catch(() => { if (initial) setBars([]); })
      .finally(() => { if (initial) setChartLoading(false); });
    setChartLoading(true);
    load(true);
    chartTimer.current = setInterval(() => load(false), tf === '1d' ? 30000 : 7000);   // 일봉은 느리게
    return () => { if (chartTimer.current) clearInterval(chartTimer.current); };
  }, [sel, tf]);

  // ① 시장 배경(외인 조류·미국 선물·VIX·코스피 종합) — 60초 폴링, 장 마감 시 자동 중단
  useEffect(() => {
    let t: ReturnType<typeof setInterval> | null = null;
    const tick = () => fetchDaytradeBackdrop().then((d) => {
      setBackdrop(d);
      setBdAt(Date.now());   // 🔄 카드 갱신 카운트다운 기준점(07-09)
      addPulse(d);   // 🫀 펄스 누적(탭 열려 있을 때 — 엔진과 합쳐 25~30초당 한 점)
      // 🧭 조건부 소리 켜짐 판정 컨텍스트 — 지금 오늘 장 성격·외인 선물 방향(±1)을 소리 켜짐 판정기에 공급(표본 30+ 국면부터 자동 사용)
      try { setLicenseCtx(d.character?.character, d.futures?.foreign != null ? (d.futures.foreign > 0 ? 1 : d.futures.foreign < 0 ? -1 : 0) : null); } catch { /* noop */ }
      if (!d.market_active && t) { clearInterval(t); t = null; }
    }).catch(() => {});
    tick();
    t = setInterval(tick, 30000);   // 07-09 60초→30초(카드 실시간화 — 서버 state_bus 25초 캐시)
    return () => { if (t) clearInterval(t); };
  }, []);

  // 🫀 핵심 게이트박동 줄 — 5초마다 nowTs 갱신(신선도 '수급 N초 전 갱신' 카운트가 폴 사이에도 흐르게).
  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);

  // 📈 스파크라인 궤적 — 60초 폴링. 최초 1회는 항상(마감 후에도 오늘 전체 곡선 표시), 장중이면 60초마다 갱신.
  const marketActive = backdrop?.market_active;
  useEffect(() => {
    let alive = true;
    const load = () => fetchDaytradeSeries().then((d) => { if (alive) setSeries(d.series || null); }).catch(() => {});
    load();
    if (marketActive === false) return () => { alive = false; };   // 마감 후엔 궤적 고정 — 폴 중단
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [marketActive]);

  // 🗞️ 상황판 2층(지금 뉴스 + 오늘의 시간표) — 5분 폴(서버 8분 캐시). 마감 후에도 최초 1회는 표시.
  useEffect(() => {
    let alive = true;
    const load = () => fetchDaytradeNews().then((d) => { if (alive) setNews(d); }).catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // 🌙 밤새 보유 판단 배너 — 밤새 보유 판단(overnight_gate) 15초 폴링. 관리자 전용 엔드포인트라 관리자만.
  useEffect(() => {
    if (!isOwner()) return;
    let alive = true;
    const load = () => fetchSniper().then((d) => { if (alive) setSniper(d); }).catch(() => { /* 배너 없으면 그냥 미표시 */ });
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // (알림 감지·푸시는 ../alerts 엔진이 App 최상위에서 항상 수행 — 메뉴 떠나도 동작)

  // ② 종목별 추정 수급 — 현재 종목 리스트로 30초 폴링(2초 시세 폴링과 분리해 부하↓)
  const tickerKey = (data?.items ?? []).map((i) => i.ticker).join(',');
  useEffect(() => {
    if (flowTimer.current) clearInterval(flowTimer.current);
    const tickers = tickerKey ? tickerKey.split(',') : [];
    if (!ready || tickers.length === 0) return;
    const poll = () => fetchDaytradeFlow(tickers).then((r) => setFlows((prev) => ({ ...prev, ...r.flows }))).catch(() => {});
    poll();
    flowTimer.current = setInterval(poll, 30000);
    return () => { if (flowTimer.current) clearInterval(flowTimer.current); };
  }, [tickerKey, ready]);

  // 핀 종목인데 계획이 없으면(이전 버전 핀 등) 실시간 값으로 자동 생성·고정.
  useEffect(() => {
    const items = data?.items ?? [];
    setPinPlans((prev) => {
      let changed = false; const next = { ...prev };
      for (const t of pinned) {
        if (!next[t]) {
          const it = items.find((i) => i.ticker === t);
          if (it && it.live && it.price) { next[t] = defaultPlan(it); changed = true; }
        }
      }
      if (changed) { try { localStorage.setItem('dt_pin_plans', JSON.stringify(next)); } catch { /* noop */ } }
      return changed ? next : prev;
    });
  }, [data, pinned]);

  const togglePush = async () => {
    if (pushOn) { setPushOn(false); await disablePush(); return; }   // 🔕 OFF = 이 기기 구독 해제 + 서버 endpoint 제거(잠금화면 푸시 원천 중단)
    setPushOn(await enablePush());
  };

  const [showAlertCfg, setShowAlertCfg] = useState(false);   // 알림 종류 on/off 패널
  const [, setCfgVer] = useState(0);                   // 토글 시 재렌더용
  const [alertWatchOnly] = useState(() => { try { return localStorage.getItem('dt_alert_watchonly') === '1'; } catch { return false; } });    // 종목 알림 — 👁 감시 종목만 보기(사운드 게이팅도 이 값 참조)
  useEffect(() => { try { localStorage.setItem('dt_alert_watchonly', alertWatchOnly ? '1' : '0'); } catch { /* noop */ } }, [alertWatchOnly]);
  const [collapseSupply] = useState(() => { try { return localStorage.getItem('dt_collapse_supply') !== '0'; } catch { return true; } });
  useEffect(() => { try { localStorage.setItem('dt_collapse_supply', collapseSupply ? '1' : '0'); } catch { /* noop */ } }, [collapseSupply]);
  // 고정 종목을 맨 위로 분리(핀 순서 유지). 이름은 저장값 우선(유니버스 밖이면 백엔드가 티커로 줌).
  const allItems = data?.items ?? [];
  const pinnedRows = pinned
    .map((t) => allItems.find((i) => i.ticker === t))
    .filter((i): i is DaytradeItem => !!i)
    .map((i) => ({ ...i, name: pinNames[i.ticker] || i.name }));

  // 🔎 검색에서 계획 없이 핀한 종목이 스캔에 실제로 잡히면(시세 확보) 그때 기본 매수 계획을 채워줌.
  useEffect(() => {
    const add: Record<string, PinPlan> = {};
    pinned.forEach((t) => {
      if (pinPlans[t]) return;
      const it = allItems.find((i) => i.ticker === t);
      if (it && it.price > 0) add[t] = defaultPlan(it);
    });
    if (Object.keys(add).length) savePinPlans({ ...pinPlans, ...add });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems, pinned]);

  // 🚪 v3 전면 개편 — 1분 증분(펄스에서 계산). KOSPI 주체 + 프로그램(pgm)만 펄스 추적(코스닥은 누적만).
  const flow1 = (key: 'frgn' | 'inst' | 'pen' | 'indi' | 'pgm'): number | null => {
    if (pulse.length < 2) return null;
    const last = pulse[pulse.length - 1];
    const tgt = last.t - 60 * 1000;
    let past: PulsePoint | null = null;
    for (const x of pulse) { if ((x as unknown as Record<string, number | undefined>)[key] == null) continue; if (x.t <= tgt) past = x; }
    if (!past) return null;
    const cur = (last as unknown as Record<string, number>)[key];
    const prev = (past as unknown as Record<string, number>)[key];
    if (cur == null || prev == null) return null;
    return cur - prev;
  };
  const km = backdrop?.markets?.KOSPI;
  const qm = backdrop?.markets?.KOSDAQ;
  const isExpiry = (backdrop?.events ?? []).some((e) => /만기/.test(e.name || '') || /만기/.test(e.note || ''));
  // 🎨 좌열 수급 기록 장부 행(B안 대담형) — 방향 세로바 + 이름 + '1분 ±N억' + 큰 숫자(단위 축소) + 궤적 스파크라인.
  //   색은 '가리키는' 것 — 숫자·바에만(양수 웜 #E06C5B·음수 쿨 #5C8FD6). 결측은 이어붙임 없이 끊음(위조 금지).
  //   valStr 오버라이드로 선물 온도(억/조 아닌 지수차) 지원. residual=아침 '오늘 집계 준비 중'.
  const ledgerRow = (label: string, cum: number | null | undefined, d1: number | null, numSize: number, sk?: keyof DaytradeSeries['series'],
    opts: { residual?: boolean; caption?: string; fill?: boolean; last?: boolean; valStr?: string | null } = {}) => {
    const { residual = false, caption = '', fill = true, last = false } = opts;
    const valStr = opts.valStr !== undefined ? opts.valStr : (cum != null ? fmtEok(cum) : null);
    const numDir = cum == null ? null : cum > 0 ? 1 : cum < 0 ? -1 : 0;
    const barC = numDir == null ? '#3A4557' : numDir > 0 ? '#E06C5B' : '#5C8FD6';
    const numColor = valStr == null ? '#5E6B82' : numDir == null || numDir === 0 ? '#E9EFF8' : numDir > 0 ? '#E06C5B' : '#5C8FD6';
    const deltaC = d1 == null || d1 === 0 ? '#525E75' : d1 > 0 ? 'rgba(224,108,91,.85)' : 'rgba(92,143,214,.85)';
    const { num, unit } = splitNumUnit(valStr);
    return (
      <div style={{ position: 'relative', padding: '12px 0 13px 13px', borderBottom: last ? 'none' : '1px solid #151D2B' }}>
        <div style={{ position: 'absolute', left: 0, top: 15, bottom: caption ? 34 : 16, width: 2, borderRadius: 1, background: barC, opacity: .85 }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 12.5, fontWeight: 550, color: '#8E9AB0' }}>{label}</span>
          {d1 != null && Math.abs(d1) >= 1 && (
            <span style={{ fontSize: 11.5, color: '#525E75' }}>1분 <span style={{ fontWeight: 650, color: deltaC }}>{fmtEok(d1)}</span></span>
          )}
        </div>
        {valStr == null && residual ? (
          <div style={{ marginTop: 6, fontSize: 11, color: '#525E75', height: numSize, display: 'flex', alignItems: 'center' }}>오늘 집계 준비 중</div>
        ) : (
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ fontSize: numSize, fontWeight: numSize >= 27 ? 760 : 750, letterSpacing: '-.015em', lineHeight: 1, color: numColor, whiteSpace: 'nowrap' }}>
              {num}{unit && <span style={{ fontSize: Math.round(numSize * 0.52), fontWeight: 600, color: numColor, opacity: .85 }}>{unit}</span>}
            </div>
            {sk && <div style={{ width: 92, flex: 'none' }}><TrendSpark pts={series?.[sk]} dir={numDir} height={22} fill={fill} /></div>}
          </div>
        )}
        {caption && <div style={{ marginTop: 5, fontSize: 11, color: '#525E75' }}>{caption}</div>}
      </div>
    );
  };
  // 🎨 코스닥 미니 셀(B안 2×2) — 이름 ←→ 값(15.5px, 방향색). 스파크·1분 델타 없음.
  const miniCell = (label: string, cum: number | null | undefined, border: boolean) => {
    const numDir = cum == null ? null : cum > 0 ? 1 : cum < 0 ? -1 : 0;
    const valStr = cum != null ? fmtEok(cum) : null;
    const numColor = valStr == null ? '#5E6B82' : numDir == null || numDir === 0 ? '#E9EFF8' : numDir > 0 ? '#E06C5B' : '#5C8FD6';
    const { num, unit } = splitNumUnit(valStr);
    return (
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 6, padding: '8px 0', borderBottom: border ? '1px solid rgba(21,29,43,.7)' : 'none' }}>
        <span style={{ fontSize: 11.5, color: '#525E75' }}>{label}</span>
        <span style={{ fontSize: 15.5, fontWeight: 700, color: numColor, whiteSpace: 'nowrap' }}>{num}{unit && <span style={{ fontSize: 11, fontWeight: 600, color: numColor, opacity: .85 }}>{unit}</span>}</span>
      </div>
    );
  };
  // 📱 폰 전용 큰손 요약 칸(08-23) — 피드를 맨 위로 올리면서, 좌열 수급 원장의 '오늘 얼마나 샀나'만
  //   한 줄로 먼저 보여준다(상세는 피드 아래 원장 그대로). 값·색 규칙은 ledgerRow와 같은 것을 쓴다.
  const stripCell = (label: string, cum: number | null | undefined) => {
    const numDir = cum == null ? null : cum > 0 ? 1 : cum < 0 ? -1 : 0;
    const valStr = cum != null ? fmtEok(cum) : null;
    const numColor = valStr == null ? '#5E6B82' : numDir == null || numDir === 0 ? '#E9EFF8' : numDir > 0 ? '#E06C5B' : '#5C8FD6';
    const { num, unit } = splitNumUnit(valStr);
    return (
      <span style={{ color: '#8E9AB0', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}{' '}
        <span style={{ fontWeight: 750, color: numColor }}>
          {valStr == null ? '—' : <>{num}{unit && <span style={{ fontSize: 10.5, fontWeight: 600, opacity: .85 }}>{unit}</span>}</>}
        </span>
      </span>
    );
  };
  const pgmCum = backdrop?.rt?.pgm_eok;
  const pgm1 = flow1('pgm');
  const basis = backdrop?.rt?.basis;
  const hyxp = backdrop?.hyx;   // 🃏 기준 시세 카드 — 하이닉스 현재가·등락%(backdrop 재사용)
  // 🌙 09-23 장 상태 꼬리표 — 거래소 저녁장(16~20시, 주식만) 개시 뒤 이 카드 가격은 저녁에도 움직인다.
  //   공식 종가(15:30)와 다른 값일 수 있음을 가격 바로 밑에 적는다. 정규장(또는 옛 서버라 session 없음)이면 아무것도 안 붙인다.
  const sess = data?.session;
  const sessTag: { chip: string; note: string; amber: boolean } | null =
    sess === 'evening' ? { chip: '저녁장 가격', note: '20시까지 주식만 거래 · ETF는 15:30 마감', amber: true }
    : sess === 'break' ? { chip: '오늘 종가', note: '저녁장은 16시에 열려요', amber: false }
    : sess === 'done' ? { chip: '저녁장 마감 가격', note: '공식 종가는 15:30 가격이에요', amber: false }
    : (sess === 'pre' || sess === 'closed') ? { chip: '장 닫힘', note: '마지막 체결 가격이에요', amber: false }
    : null;
  const hyxPriceColor = hyxp?.price == null ? '#5E6B82' : hyxp.pct == null || hyxp.pct === 0 ? '#E9EFF8' : hyxp.pct > 0 ? '#E06C5B' : '#5C8FD6';   // 기준 시세 가격 방향색(등락 부호)
  // 🫀 핵심 게이트박동(신선도) — 서버가 실어보낸 실측 나이(초) + 마지막 수신(bdAt) 이후 경과. 180초 초과 시 앰버.
  const srcAgeNow = backdrop?.src_age != null ? Math.max(0, Math.round(backdrop.src_age + (nowTs - bdAt) / 1000)) : null;
  const gate = sniper?.gate ?? null;
  // 🎨 규격 V2(08-23) — 밤새 보유 판단 배너 색. 상태 3단(전 화면 공통)과 같은 뜻·같은 톤:
  //   좋음(들고 자도 될 만한 밤) = ok · 주의/중립 = 앰버 · 멈춤(피하는 게 나은 밤) = warn(경보 빨강 금지).
  //   up(상승색)은 방향 전용이라 '멈춤'에 쓰지 않는다 — 오르는 중이라는 뜻으로 읽힌다.
  const gc = gate?.signal === 'green' ? { b: 'border-dc-ok/45 bg-dc-ok/[0.08]', t: 'text-dc-ok' }
    : gate?.signal === 'red' ? { b: 'border-dc-warn/45 bg-dc-warn/[0.08]', t: 'text-dc-warn' }
      : { b: 'border-dc-point/40 bg-dc-point/[0.08]', t: 'text-dc-point' };

  // 🎨 피드 필터(B안 프로토타입 선택 기능 이식, 규칙 4) — 전체 / 뉴스 제외 / 큰손만.
  const [feedFilter, setFeedFilter] = useState<'전체' | '뉴스 제외' | '큰손만'>('전체');
  // 🎨 범례 축약(08-23 규격 V2) — 12종을 한 줄에 다 늘어놓으면 폰에서 3줄을 먹고 제목을 밀어낸다.
  //   자주 보는 앞 6종만 기본으로, 나머지는 '+N'을 누르면 펼친다(표시만 — 알림 종류·색·판정은 불변).
  const [legendAll, setLegendAll] = useState(false);
  const shownAlerts = alerts.filter((a) => feedFilter === '뉴스 제외' ? a.kind !== 'news' : feedFilter === '큰손만' ? (a.kind === 'flow_buy' || a.kind === 'flow_sell') : true).slice(0, 200);

  return (
    <div className="dc-root flex flex-1 flex-col min-h-0 overflow-hidden" style={{ background: 'radial-gradient(1100px 480px at 50% -140px,rgba(220,168,94,.05),transparent 60%),linear-gradient(#0B0F17,#080B11)' }}>
      {/* 유틸리티 바 — 디자인 글로벌 바의 버튼군(사용법·알림·사운드·소리 테스트·종류)은 공용 Header(다른 탭 공유)가 아니라 여기(단타탭 스코프)에 둔다. 🔊 소리 테스트 = 고스트 버튼 자리 유지(규칙 2). */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 7, minHeight: 44, padding: '7px 16px', borderBottom: '1px solid #141B28', flex: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginLeft: 'auto', flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: '100%' }}>
          <button onClick={() => setShowGuide((g) => !g)} className="dc-ghost" style={DC_GHOST}>{DcIcon.info(12)} 사용법</button>
          <button onClick={togglePush} title={pushOn ? '알림 끄기 — 이 기기의 잠금화면 푸시가 완전히 멈춰요(다른 기기는 그대로).' : '알림 켜기 — 큰손·큰손 매수 시작·핀 손절/목표·감시 종목을 폰 잠금화면 푸시(앱 닫아도 옴).'} className={pushOn ? undefined : 'dc-ghost'} style={pushOn ? DC_ON : DC_GHOST}>{DcIcon.bell(12, !pushOn)} 알림</button>
          <button onClick={() => { const n = !soundOn; setSoundEnabled(n); setSoundOn(n); }} title="알림 사운드 on/off" className={soundOn ? undefined : 'dc-ghost'} style={soundOn ? DC_ON : DC_GHOST}>{DcIcon.speaker(12, !soundOn)} 사운드</button>
          <button onClick={() => playChime('up')} title="소리가 나는지 지금 바로 확인 — 누르면 샘플 알림음이 한 번 울려요. (브라우저는 페이지를 처음 클릭하기 전엔 소리를 막는데, 이 버튼이 그 잠금도 같이 풀어줘요.)" className="dc-ghost" style={DC_GHOST}>{DcIcon.eq(12)} 소리 테스트</button>
          <button onClick={() => setShowAlertCfg((v) => !v)} className={showAlertCfg ? undefined : 'dc-ghost'} style={showAlertCfg ? DC_ON : DC_GHOST}>{DcIcon.gear(12)} 종류</button>
        </div>
      </div>

      {/* ⚙ 푸시 종류(이 기기) — 화면 알림 4종만 */}
      {showAlertCfg && (
        <div className="px-4 py-2 border-b border-dc-hair bg-dc-bg0/50 text-[11.5px] space-y-1.5">
          {isPushEnabled() ? (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-dc-ink3">📲 이 기기 푸시:</span>
              {PUSH_CAT_META.map(({ cat, icon, label }) => {
                const on = getPushPref()[cat];
                return (
                  <button key={cat} onClick={() => { setPushPref(cat, !on); setCfgVer((v) => v + 1); }}
                    className={`px-2 py-0.5 rounded-full border ${on ? 'border-dc-point/40 text-dc-point bg-dc-point/10' : 'border-[#202A3C] text-dc-ink3'}`}>
                    {icon} {label} {on ? '켬' : '끔'}
                  </button>
                );
              })}
            </div>
          ) : <p className="text-dc-ink3">🔕 지금 <b>알림</b>이 꺼져 있어 <b className="text-dc-ink2">이 기기로는 잠금화면 푸시도 오지 않아요</b>. 위 <b>알림</b>을 켜면 다시 오고, 푸시 종류도 고를 수 있어요.</p>}
          <p className="text-dc-ink3 text-[10.5px] leading-snug">이 도우미는 검증된 4종(큰손·큰손 매수 시작·핀·감시)만 알려요. 나머지 신호는 검증 중으로 계속 성적을 쌓고, 도움된다고 판단되면 하나씩 승격됩니다.</p>
        </div>
      )}

      {/* 사용법 가이드 */}
      {showGuide && (
        <div className="px-4 py-3 border-b border-dc-hair bg-dc-bg0/50 text-[11.5px] text-dc-ink2 space-y-2">
          <div className="flex items-center justify-between">
            <b className="text-dc-ink1">단타 도우미 사용법</b>
            <button onClick={dismissGuide} className="text-dc-ink3 hover:text-dc-ink2"><X size={13} /></button>
          </div>
          <p>이 화면은 <b className="text-dc-ink1">실제로 도움된 알림만</b> 남긴 새 단타 도우미예요. 검증된 것만 화면·소리·푸시로 옵니다:</p>
          <ul className="list-disc pl-4 space-y-0.5 text-dc-ink2">
            <li><b className="text-dc-ink1">🏦 큰손 수급</b> — 외국인·기관·연기금이 코스피/코스닥을 크게 사거나 파는 순간. <b className="text-dc-ink2">⚠️ 수급(외국인·기관) 숫자는 한국거래소(KRX) 정규장 집계 기준이에요</b> — 요즘 주요 종목 거래의 <b>3~4할</b>이 오가는 <b>대체거래소(NXT)</b> 물량은 <b>어떤 창구에도 집계되지 않아요</b>(증권사·거래소 어디서도 안 줍니다). 그래서 "큰손이 조용하다"가 정말 조용한 게 아니라 <b>안 보이는 곳에서 오갔을 수</b> 있어요.</li>
            <li><b className="text-dc-ink1">⏩ 큰손 매수 시작</b> — 큰손이 사들이기 시작하는 가장 이른 조짐(진입 방아쇠). <b>90초 확인을 거쳐 옵니다(조금 늦게, 더 정확하게)</b> — 조짐이 뜬 뒤 90초에도 같은 방향이 이어질 때만 화면에 올라와요. 이어지지 않으면 아무 말도 하지 않아요. <b>큰손 매도 시작은 화면에 안 나옵니다</b>(성적이 아직 모자라 검증 중으로 채점만 해요).</li>
            <li className="text-dc-point/90">📣 <b>⏩ 큰손 매수 시작은 '90초 확인'을 거치게 바뀌었어요</b>(2026-08-04 밤) — 이전엔 조짐이 뜨자마자 알렸는데, 이제 <b>90초 뒤에도 같은 방향이면</b> 그때 알려드려요(성적이 더 좋았던 방식). 검증은 계속 쌓는 중이라 카드에 <b>잠정</b> 표시가 붙어요 — 지금 성적은 📊 통계 탭의 <b>승격 심사</b>에서 보세요. <b>2026-08-08부터 화면에 오는 건 '매수' 쪽 하나뿐이에요</b> — 잠깐 같이 나오던 매도 쪽은 성적(6번 중 2번)이 모자라 검증 중으로 돌려보냈어요. 확인 전 원본(비교용)과 큰손·하이닉스 힘 감지·종합 신호도 그대로 검증 중으로 성적을 쌓습니다. 잠금화면 푸시는 계속 안 보내요(화면·PC 소리만).</li>
            <li className="text-dc-point/90">🌅 <b>'아침 판정'이 새로 들어왔어요</b>(2026-08-04) — 매 거래일 <b>개장 전 08:50</b>에 간밤 미국과 달력만 보고 오늘이 어떤 날인지 한 번 말해드려요. ①<b>추격 금지</b>(간밤 나스닥 급등 → 하이닉스 따라 사지 말기) ②<b>매수 후보</b>(미 반도체·엔비디아 동반 폭락) ③<b>인버스 우위</b>(FOMC 다음날) ④<b>되돌림 후보</b>(어제 하이닉스 급락 + 장이 평소만큼 출렁일 때만) ⑤<b>코스닥 만기 전날</b>(만기 전날 오후 3시). 조건에 맞는 날만 울리니 <b>아무 말 없는 아침이 정상</b>이에요. 각 카드에 지난 성적(10번 중 몇 번)을 함께 적어드리고, 전부 <b>시험 중</b>이라 참고용입니다.</li>
            <li className="text-dc-point/90">🌙 <b>'급락일 밤 반등'이 새로 들어왔어요</b>(2026-08-20) — <b>코스피가 오늘 2% 넘게 빠진 날</b>, 마감 직전 <b>오후 3시 20분</b>에 딱 한 번 알려드려요. 2001년부터 <b>26년치를 전부</b> 세어 보니, 그런 날 밤(오늘 종가 → 다음 날 아침 시가)은 <b>290번 중 10번에 6.6번</b> 올랐어요(평균 <b>+0.33%</b>). 아무 밤이나 들고 자는 것(10번에 5.9번)보다 뚜렷이 나았고, 5년씩 <b>다섯 토막으로 잘라도 다섯 번 다</b> 그랬어요 — 한 시기가 만든 착시가 아니에요. <b>⚠️ 안심하라는 말이 아니라 '반등에 걸어볼 만한 밤'이라는 뜻이에요</b> — 이길 땐 조금(+0.33%) 벌고 <b>빗나갈 땐 크게 빠집니다</b>(나쁜 밤 상위 5% 평균 <b>−3.9%</b>, 가장 나빴던 밤 <b>−6.1%</b>). <b>금요일 밤·연휴 전날 밤은 성적이 나빠서 일부러 아무 말도 안 해요</b>. 실전 성적은 다음 날 아침 시가로 채점돼서 아직 <b>잠정</b>이고, 폰 잠금화면 알림은 안 보내요(화면·PC 소리만). 성적이 <b>10번 중 6번 아래</b>로 내려오면 다시 내릴지 여쭤볼게요.</li>
            <li className="text-dc-point/90">🐋 <b>'큰손 매수 뒤 하이닉스 반응'이 새로 들어왔어요</b>(2026-08-15) — 외국인·기관·연기금이 <b>코스피를 크게 사기 시작한 직후</b>, 5분 안에 <b>SK하이닉스가 실제로 +0.2% 따라 오르면</b> 그 순간 알려드려요. "큰손이 사니까 반도체가 진짜 움직인다"를 <b>눈으로 확인한 자리</b>예요. 2026-07-21부터 조용히 성적만 쌓아 왔는데 <b>한 달 만의 첫 합격자</b>라서 화면에 올렸어요 — 지금까지 이런 순간은 <b>5분 뒤에도 10번 중 8.6번</b> 이어졌어요(35번 기준). <b>⚠️ 대신 이 알림은 하이닉스가 이미 +0.2% 오른 걸 보고 우는 구조라 그만큼 늦어요</b> — 그 몫과 사고팔 때 드는 비용까지 다 빼도 10번 중 <b>7.3번</b>은 이어졌습니다. <b>✅ 2026-08-22부터는 '잠정' 딱지를 뗐어요</b> — 35번을 재 보니 성적이 흔들려도 기준(10번 중 7번)을 밑돌 가능성이 낮다고 나와서 <b>합격</b>으로 확정했어요. 폰 잠금화면 알림은 여전히 안 보내요(화면·PC 소리만). 합격했어도 채점은 계속하고, 성적이 <b>10번 중 6번 아래</b>로 내려오면 다시 내릴지 여쭤볼게요.</li>
            <li><b className="text-dc-ink1">🎖️ 종합 신호</b> — 여러 근거가 겹칠 때만 우는 신호예요. SK하이닉스에서 여러 근거(호가 벽·체결 쏠림·선물·큰손 수급·시간대·장세·뉴스)가 <b>한 방향으로 겹치는 순간</b>만 골라 울려요. 자주 안 울리는 대신 확신이 높을 때만 나오고, <b>무엇이 겹쳤는지 알림에 함께</b> 적어드려요 · <b>시험 중</b>(성적 쌓는 중이라 참고용) · 잠금화면 푸시는 안 보내요(화면·PC 소리만).</li>
            <li><b className="text-dc-ink1">⚡ 하이닉스 힘 감지</b> — SK하이닉스 주문 압력(호가)과 실제 체결이 같은 방향으로 <b>평소보다 세게</b> 쏠릴 때(매수/매도 힘의 초입 후보, 레버리지·인버스 참고). 같은 방향이 이어지면 <b>카드 한 장이 제자리 갱신</b>(지금 몇 분째·몇 번째), 방향이 바뀌면 새 카드로 · <b>시험 중 신호</b>(잠정 기준값 — 며칠 표본으로 다듬는 중).</li>
            <li className="text-dc-point/90">🔕 <b>'지수 급락 조기 감지'는 화면에서 내렸어요</b>(2026-08-04) — 코스피·코스닥이 <b>2분 만에 조금 밀릴 때</b> 뜨던 "급락 조짐"이 하루 45번씩 울려서, 정작 <b>진짜 급락</b>이 그 소음에 묻혔어요. 이제 <b>4분 만에 크게 밀리는 확정 급락만</b> 화면·소리로 옵니다. 없앤 게 아니라 검증 중으로 성적만 계속 쌓아요(📊 통계 탭에서 봐요).</li>
            <li><b className="text-dc-ink1">📌 핀 손절/목표</b> · <b className="text-dc-ink1">👁 감시 종목</b> — 내가 지정한 종목 소식</li>
            <li className="text-dc-point/90">📄 <b>'공시 경고'가 새로 들어왔어요</b>(2026-08-12) — <b>내가 가진 종목</b>(📌핀·👁감시·내 현황 탭 보유)에 주가를 누르는 공시 <b>세 가지</b>가 뜨면 알려드려요: <b>전환사채 발행</b> · <b>유상증자</b> · <b>자사주 처분</b>. <b>2022년 이후</b> 기록으로 이런 공시 뒤 20거래일은 10번 중 <b>5.8~6.7번 내렸어요</b>(한가운데 성적 −2.7~−7.1%). 이 세 가지는 <b>10건 중 6건이 장 마감 뒤</b>에 나오는데, 다음 날 시가까지 밤사이 벌어진 틈이 거의 0이라 <b>아침에 정리해도 통계상 늦지 않아요</b> — 그래서 밤사이 공시는 <b>아침 8시 35분</b>에 몰아서, 장중 공시는 <b>30분마다</b> 확인해 알려드려요. 카드의 <b>원문 보기 ↗</b>로 공시 원문을 바로 열 수 있어요. 실전 성적은 20거래일 뒤에 채점돼서 아직 <b>잠정</b>이고, 폰 잠금화면 알림은 안 보내요(화면·PC 소리만).</li>
            <li><b className="text-dc-ink1">🗞️ 뉴스 속보</b> — 연기금 리밸런싱·반도체 대형(하이닉스·HBM)·공매도/관세처럼 <b>신호의 의미를 바꾸는 뉴스</b>를 제목 그대로 바로(해석 없음). 따로 찾아보다 늦지 않게 기본으로 폰까지 알려요(⚙에서 끌 수 있어요).</li>
            <li><b className="text-dc-ink1">🔌 데이터 끊김·복구</b> — 서버가 받아오는 <b>체결 시세·수급·지수 시세</b>가 장중에 끊기면 "끊김", 다시 들어오면 "복구(몇 분 끊겼었음)"를 <b>한 번씩</b> 알려요. <b>알림이 안 오는 게 시장이 조용해서인지, 데이터가 죽어서인지</b> 구분하려고요. 화면과 PC 소리로만 오고 폰 잠금화면에는 안 보내요. <b>한계</b> — 이건 <b>바깥에서 오는 데이터(증권사·네이버)가 끊겼는지</b>를 보는 감시라, <b>서버 자신이 죽으면 감시도 같이 죽어서 아무것도 못 알려요</b>(그땐 화면이 통째로 안 열려요). 체결 시세가 끊겼다고 뜨면 서버 문제일 수도, <b>거래소가 멈춘 것</b>(서킷브레이커 등)일 수도 있어요.</li>
          </ul>
          <p className="text-dc-ink3 text-[11.5px]">오른쪽 <b className="text-dc-ink2">🖥️ 상황판</b>(넓은 화면)엔 SK하이닉스·코스피 <b>기준 시세</b>, <b>오늘의 시간표</b>(FOMC·CPI·만기 등), <b>지금 뉴스</b>가 있어요 — 🔔 딱지는 위 뉴스 알림으로 뜬 기사예요.</p>
          <p><b className="text-dc-ink1">왼쪽 🏦 큰손 카드</b>는 오늘 외국인·기관·연기금·개인이 얼마나 샀는지(+1분 속도)를 보여줘요(개장 직후 <b className="text-dc-ink2">오늘 집계 준비 중</b>은 아직 오늘 첫 집계 전이라 어제 숫자를 안 띄운다는 뜻 · <b className="text-dc-ink2">한국거래소(KRX) 정규장 집계 기준</b>이라 대체거래소(NXT) 거래분은 빠져 있어요), <b className="text-dc-ink1">🌙 밤새 보유 판단</b>은 마감 무렵 "오늘 종가에 들고 자도 될지"를 알려줘요. <b className="text-dc-ink1">알림 피드</b>는 시간순 세로 타임라인이에요 — 왼쪽 점 색이 방향(빨강 매수·상승, 파랑 매도·하락, 회색 중립, 🗞️뉴스 호박), 카드엔 굵은 제목(누가·무슨 일)·설명 문장·맨 밑 시각과 수급이 담겨요.</p>
          <p className="text-dc-ink3 text-[11.5px]">종목 발굴·차트는 <b>전체 스캔</b>·<b>관심종목</b> 탭에서, 핀·감시 추가는 아래 <b>내 핀·감시 관리</b>에서.</p>
        </div>
      )}

      {/* 본문 — 🖥️ 3열 콘솔(B안 대담형): 좌 수급 기록 장부 | 중 알림 피드(주연) | 우 텍스트 레일.
          lg+ 324|1fr|312 그리드(각 열 개별 스크롤·헤어라인 경계), 좁으면 세로 스택(단일 스크롤 — 기존 반응형 보존, 규칙 3). */}
      <div className="dc-body dc-scroll">

        {/* 📱 큰손 요약 스트립 — 폰(<1024)에서만. 넓은 화면은 좌열 원장이 그 자리라 dc.css에서 display:none.
            알림 피드를 첫 화면으로 올리되 '오늘 큰손이 얼마나 샀나'는 한 줄로 먼저 말해준다(시안 Mobile 보드). */}
        <div className="dc-strip" style={{ alignItems: 'center', gap: 10, padding: '9px 12px', borderBottom: '1px solid #141B28', whiteSpace: 'nowrap', fontSize: 12, flex: 'none' }}>
          <span style={{ color: '#5E6B82', fontSize: 10.5, fontWeight: 650, letterSpacing: '.08em', flexShrink: 0 }}>큰손</span>
          <div style={{ flex: 1, minWidth: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 14px' }}>
            {stripCell('외국인', km?.foreign)}
            {stripCell('기관', km?.institution)}
            {stripCell('연기금', km?.pension)}
            {stripCell('개인', km?.individual)}
          </div>
        </div>

        {/* ◀ 좌열 — 수급 기록 장부 (코스피 큰손 4행 · 코스닥 2×2 · 지금 이 순간) */}
        <div className="dc-col dc-col-left dc-pad dc-scroll">
          <div style={{ ...DC_SECHEAD, marginBottom: 2 }}>{DcIcon.bank(13)}<span style={DC_SECLABEL}>코스피 큰손</span></div>
          {ledgerRow('외국인', km?.foreign, flow1('frgn'), 27, 'frgn', { residual: km?.residual })}
          {ledgerRow('기관', km?.institution, flow1('inst'), 27, 'inst', { residual: km?.residual })}
          {ledgerRow('연기금', km?.pension, flow1('pen'), 27, 'pen', { residual: km?.residual })}
          {ledgerRow('개인', km?.individual, flow1('indi'), 27, 'indi', { residual: km?.residual, last: true })}
          {!backdrop && <p style={{ fontSize: 11, color: '#525E75', marginTop: 10 }}>시장 배경 불러오는 중…</p>}

          <div style={{ height: 1, background: '#141B28', margin: '8px -18px 14px' }} />
          <div style={DC_SECLABEL}>코스닥</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 18, marginTop: 4 }}>
            {miniCell('외국인', qm?.foreign, true)}
            {miniCell('기관', qm?.institution, true)}
            {miniCell('연기금', qm?.pension, false)}
            {miniCell('개인', qm?.individual, false)}
          </div>

          <div style={{ height: 1, background: '#141B28', margin: '10px -18px 14px' }} />
          <div style={DC_SECHEAD}>{DcIcon.pulse(13)}<span style={DC_SECLABEL}>지금 이 순간</span></div>
          {ledgerRow('프로그램 매매', pgmCum ?? null, pgm1, 23, 'pgm')}
          {ledgerRow('선물 온도', basis ?? null, null, 23, 'basis', { valStr: basis != null ? `${basis >= 0 ? '+' : '−'}${Math.abs(basis).toFixed(2)}` : null, fill: false, caption: '선물이 현물보다 비싼 정도', last: true })}

          {/* 만기일 경계 — 데이터 있을 때만(실기능 보존) */}
          {isExpiry && (
            <p className="text-[11.5px]" style={{ marginTop: 14, padding: '10px 12px', borderRadius: 10, lineHeight: 1.5, color: '#E8BC78', background: 'rgba(220,168,94,.04)', border: '1px solid rgba(220,168,94,.25)' }}>
              📅 오늘 만기일 — 마감 무렵(특히 폐장 전 10분) 출렁임 주의
            </p>
          )}

          {/* 🌙 밤새 보유 판단 — 관리자·gate 있을 때만(15:15~개장 전, pending 숨김). 성적 표시 없음 */}
          {isOwner() && gate && gate.signal !== 'pending' && (
            <div className={`rounded-lg border ${gc.b} px-3 py-2.5`} style={{ marginTop: 14 }}>
              <div className="flex items-center gap-2 flex-wrap">
                <b className={`text-[13px] ${gc.t}`}>{gate.title}</b>
                <span className="ml-auto text-[10.5px]" style={{ color: '#525E75' }}>🌙 밤새 보유 판단 · {gate.as_of}</span>
              </div>
              {gate.why.map((w, i) => <p key={i} className={`text-[10.5px] leading-snug mt-0.5 ${i === 0 ? gc.t : ''}`} style={i === 0 ? undefined : { color: '#8E9AB0' }}>{w}</p>)}
              {gate.exp_close && (
                <p className="text-[10.5px] mt-1" style={{ color: '#B5C0D2' }} title="장 마감 동시호가(15:20~15:30) 예상체결가 — 지금 이대로 끝나면">
                  📉 지금 이대로면 마감: 하이닉스 <b className="dc-mono">{gate.exp_close.price.toLocaleString('ko-KR')}원</b>{' '}
                  <b className="dc-mono" style={{ color: gate.exp_close.chg >= 0 ? '#E06C5B' : '#5C8FD6' }}>({gate.exp_close.chg >= 0 ? '+' : ''}{gate.exp_close.chg}%)</b>
                </p>
              )}
              {gate.tomorrow && gate.tomorrow.events.length > 0 && <p className="text-[10.5px] mt-1" style={{ color: '#E8BC78' }}>📅 내일: {gate.tomorrow.events.join(' · ')}</p>}
            </div>
          )}
        </div>

        {/* ● 중앙 — 알림 피드(주연): 스티키 블러 헤더 + 시간대 기준값 + 카테고리 노드 위계 타임라인 */}
        <div className="dc-col dc-col-feed dc-scroll" style={{ position: 'relative' }}>
          <div className="dc-feedhead" style={{ position: 'sticky', top: 0, zIndex: 5, background: 'rgba(11,15,23,.88)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', padding: '13px 20px 10px', borderBottom: '1px solid #141B28' }}>
            <div style={{ fontSize: 10.5, fontWeight: 650, letterSpacing: '.12em', color: '#4E5A70' }}>단타 도우미 — 큰손 수급 알림 · 밤새 보유 판단</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 14px', marginTop: 6 }}>
              <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-.015em', color: '#E9EFF8' }}>알림 피드</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginLeft: 4 }}>
                {(legendAll ? DC_LEGEND : DC_LEGEND.slice(0, 6)).map(({ label, dot }) => (
                  <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 500, color: '#525E75' }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot }} />{label}
                  </span>
                ))}
                {DC_LEGEND.length > 6 && (
                  <button onClick={() => setLegendAll((v) => !v)} data-active={legendAll ? '1' : undefined} className="dc-filter"
                    title={legendAll ? '범례 접기' : `나머지 알림 종류 ${DC_LEGEND.length - 6}가지 더 보기`}
                    style={{ height: 18, padding: '0 6px', borderRadius: 5, fontSize: 10.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                      border: `1px solid ${legendAll ? 'rgba(220,168,94,.35)' : '#202A3C'}`, background: legendAll ? 'rgba(220,168,94,.1)' : 'transparent', color: legendAll ? '#DCA85E' : '#8E9AB0' }}>
                    {legendAll ? '▾ 접기' : `+${DC_LEGEND.length - 6}`}
                  </button>
                )}
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: '#525E75', flexWrap: 'wrap' }}>
                {/* 🫀 신선도·감지 LED — 서버 데이터 실제 나이 기반(실데이터, 규칙 1). 180초 초과 시 지연 경고. */}
                {backdrop?.market_active && srcAgeNow != null && (srcAgeNow > 180 ? (
                  <span style={{ color: '#E8BC78' }} title="수급·감지 데이터가 얼마나 최신인지(서버가 소스를 마지막으로 성공 갱신한 시각 기준)">⚠ 데이터 지연 중</span>
                ) : (
                  <>
                    <span>수급 <b style={{ color: '#8E9AB0', fontWeight: 600 }}>{srcAgeNow}초 전</b> 갱신</span>
                    <span style={{ color: '#2C3749' }}>·</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} title="감지 엔진 정상 동작 중">
                      <span className="dc-led" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#58BE89' }} />
                      <b style={{ color: '#8E9AB0', fontWeight: 600 }}>감지 정상</b>
                    </span>
                    <span style={{ color: '#2C3749' }}>·</span>
                  </>
                ))}
                <span className="dc-mono" style={{ display: 'inline-flex', alignItems: 'center', height: 24, padding: '0 9px', border: '1px solid #232D3F', borderRadius: 6, background: '#10161F', fontSize: 12, fontWeight: 650, color: '#E9EFF8' }}>{alerts.length}건</span>
              </div>
            </div>
            {/* 피드 필터(규칙 4) — 헤더에 작게. 문구·로직 불변(표현 필터만). */}
            <div style={{ display: 'flex', gap: 5, marginTop: 8 }}>
              {(['전체', '뉴스 제외', '큰손만'] as const).map((f) => {
                const on = feedFilter === f;
                return (
                  <button key={f} onClick={() => setFeedFilter(f)} data-active={on ? '1' : undefined} className="dc-filter"
                    style={{ height: 24, padding: '0 10px', borderRadius: 6, fontSize: 11, fontWeight: on ? 600 : 500, cursor: 'pointer', whiteSpace: 'nowrap',
                      border: `1px solid ${on ? 'rgba(220,168,94,.35)' : '#202A3C'}`, background: on ? 'rgba(220,168,94,.1)' : 'transparent', color: on ? '#DCA85E' : '#8E9AB0' }}>{f}</button>
                );
              })}
            </div>
          </div>

          <div className="dc-feedwrap" style={{ padding: '10px 20px 24px 14px' }}>
            {shownAlerts.length === 0 && (
              <p style={{ padding: '32px 8px', textAlign: 'center', fontSize: 12, color: '#525E75' }}>
                {alerts.length === 0 ? '아직 알림이 없어요 — 큰손이 크게 움직이거나 핀·감시 종목에 신호가 오면 여기 뜹니다.' : '이 필터에 해당하는 알림이 없어요.'}
              </p>
            )}
            {shownAlerts.map((a, idx, arr) => {
              const cat = alertCat(a);
              const d = alertDir(a);
              const major = isMajorAlert(a);
              const { head, body, aux } = splitAlert(a.msg);
              const watched = !!a.ticker && isWatched(a.ticker);
              const isNew = hlIds.has(a.id);
              const isNews = a.kind === 'news';
              const clock = (a.time || '').replace('오전 ', '').replace('오후 ', '');
              const chips = aux ? aux.split(' · ').map((s) => s.trim()).filter(Boolean) : [];
              const node = dcNode(cat, d);
              const h = hourOf(a);
              const showMarker = h >= 0 && (idx === 0 || h !== hourOf(arr[idx - 1]));
              const isLatest = idx === 0;
              const isLastRow = idx === arr.length - 1;
              // 🏦 큰손만 상태어(매수/매도 급증)를 방향색으로 — 급락·큰손 매수 시작·핀·감시 제목은 ink1(디자인). 뉴스는 별도 키커.
              const headColor = (a.kind === 'flow_buy' || a.kind === 'flow_sell') ? (d > 0 ? '#E06C5B' : '#5C8FD6') : '#E9EFF8';
              // 이름이 제목(head)과 같으면(예: 지수 급락 'marketmove'는 name==head) 중복 표기 방지 — 제목 1회만(디자인). 문구 불변.
              const showName = !!a.name && a.name !== head;
              return (
                <Fragment key={a.id}>
                  {/* 시간대 기준값 — 시(hour) 바뀌는 지점에 'N시' + 다이아 + 그라데이션 룰 */}
                  {showMarker && (
                    <div className="dc-hourmark" style={{ display: 'grid', gridTemplateColumns: '56px 30px minmax(0,1fr)', columnGap: 10, alignItems: 'center', margin: '6px 0 2px' }}>
                      <div className="dc-mono" style={{ textAlign: 'right', fontSize: 10.5, fontWeight: 600, letterSpacing: '.06em', color: '#404C61' }}>{h}시</div>
                      <div><div style={{ width: 5, height: 5, transform: 'rotate(45deg)', background: '#2C374B', margin: '0 auto' }} /></div>
                      <div><div style={{ height: 1, background: 'linear-gradient(90deg,#1A2334,transparent)' }} /></div>
                    </div>
                  )}
                  <div className="dc-feedrow" style={{ display: 'grid', gridTemplateColumns: '56px 30px minmax(0,1fr)', columnGap: 10 }}>
                    {/* 시각 거터 */}
                    <div className="dc-mono dc-feedtime" style={{ paddingTop: isNews ? 13 : 15, textAlign: 'right', fontSize: 11, fontWeight: 500, color: '#525E75' }}>{clock}</div>
                    {/* 스파인 — 세로축 + 노드(라인 펀치아웃) */}
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', left: 14.5, top: 0, bottom: 0, width: 1, background: '#161F30' }} />
                      {isNews ? (
                        <div style={{ position: 'absolute', left: 11.5, top: 17, width: 7, height: 7, borderRadius: '50%', background: '#76839A', boxShadow: '0 0 0 3px #0A0E16', zIndex: 1 }} />
                      ) : (
                        <span style={{ position: 'absolute', left: 3, top: 13, display: 'inline-flex', width: 24, height: 24, borderRadius: 7, background: node.bg, border: `1px solid ${node.border}`, color: node.color, alignItems: 'center', justifyContent: 'center', zIndex: 1, boxShadow: '0 0 0 3px #0A0E16' }}>{node.icon}</span>
                      )}
                      {/* 링 펄스 — 최신 1건에만(디자인) */}
                      {isLatest && <span className="dc-ping" style={{ position: 'absolute', left: 11.5, top: 17, width: 7, height: 7, borderRadius: '50%', pointerEvents: 'none', zIndex: 2 }} />}
                    </div>
                    {/* 내용 — 신규 도착만 진입 모션(dc-in) */}
                    <div className={isNew ? 'dc-feedbody dc-in' : 'dc-feedbody'} style={{ padding: isNews ? '11px 4px 12px 0' : '13px 4px 14px 0', borderBottom: isLastRow ? 'none' : '1px solid rgba(20,27,40,.8)' }}>
                      {isNews ? (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 11.5, fontWeight: 650, color: '#8E9AB0' }}>뉴스</span>
                            <span style={{ color: '#39445A', fontSize: 10 }}>·</span>
                            <span style={{ fontSize: 11.5, fontWeight: 650, color: '#DCA85E' }}>신호에 영향</span>
                            {watched && <span style={{ fontSize: 10, color: '#8B99AF' }} title="감시 종목">👁</span>}
                          </div>
                          <div style={{ marginTop: 5, fontSize: 14, fontWeight: 550, lineHeight: 1.5, color: '#A8B3C6' }}>
                            {showName && <span>{a.name}</span>}{showName && head ? ' · ' : ''}{head}
                            {body && <span style={{ color: '#525E75', fontWeight: 500 }}> {body}</span>}
                          </div>
                          {(chips.length > 0 || a.url) && (
                            <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {chips.map((c, i) => <span key={i} style={DC_CHIP}>{renderChipText(c, d)}</span>)}
                              {a.url && <a href={a.url} target="_blank" rel="noopener noreferrer" className="dc-linkchip" style={DC_LINKCHIP}>원문 보기 ↗</a>}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                            {showName && <span style={{ fontSize: major ? 17.5 : 14, fontWeight: 750, letterSpacing: '-.015em', color: '#E9EFF8' }}>{a.name}</span>}
                            {showName && head && <span style={{ color: '#39445A', fontSize: 12 }}>·</span>}
                            {head && <span style={{ fontSize: major ? 17.5 : 14, fontWeight: 750, letterSpacing: '-.015em', color: headColor }}>{head}</span>}
                            {/* 🏷️ 잠정 — 08-04 화면에 올라온 신호들은 아직 시험 중(최종 합격 전)이라는 표시.
                                화면에 떴다고 검증이 끝난 신호로 읽히지 않게 카드마다 작게 계속 말해준다.
                                ⏩ 90초 확인(90초)·🌅 아침 판정 5종도 같은 표시 — 표본이 얇다는 사실을 카드가 직접 말한다.
                                🐋 큰손 매수 뒤 하이닉스 반응(flowreact)은 **08-22 최종 합격 결재로 여기서 빠졌다** —
                                   서버 승격 심사(debut_board.CERTIFIED_BY_APPROVAL)와 짝이다. 되돌리려면 아래 조건에 다시 넣을 것. */}
                            {(a.kind === 'flowearly' || a.kind === 'flowearly_confirmed' || a.kind.startsWith('morning_') || a.kind === 'expiry_kosdaq' || a.kind === 'disclosure_warning' || a.kind === 'night_rebound') && (
                              <span title="시험 중인 알림이에요 — 성적이 좋아 화면에 먼저 올렸지만 70% 최종 합격은 아직 진행 중이에요. 지금 성적은 📊 통계 탭의 승격 심사에서 볼 수 있어요."
                                style={{ display: 'inline-flex', alignItems: 'center', height: 18, padding: '0 6px', borderRadius: 5, fontSize: 10.5, fontWeight: 600, whiteSpace: 'nowrap', border: '1px solid rgba(220,168,94,.35)', background: 'rgba(220,168,94,.1)', color: '#DCA85E' }}>잠정</span>
                            )}
                            {a.star && <span style={{ fontSize: 10, color: '#DCA85E' }} title="확신 신호">★</span>}
                            {watched && <span style={{ fontSize: 10, color: '#8B99AF' }} title="감시 종목">👁</span>}
                          </div>
                          {body && <div style={{ marginTop: 7, fontSize: major ? 15.5 : 14, lineHeight: 1.55, color: '#B5C0D2' }}>{renderBody(body, d, major ? 16.5 : 14.5)}</div>}
                          {/* 📄 공시 경고(08-12) — 원문 링크는 뉴스 카드와 같은 '원문 보기 ↗' 칩으로.
                              긴 주소를 본문에 그대로 쓰면 폰(375px)에서 줄이 삐져나온다. */}
                          {(chips.length > 0 || a.url) && (
                            <div style={{ marginTop: 9, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {chips.map((c, i) => <span key={i} style={DC_CHIP}>{renderChipText(c, d)}</span>)}
                              {a.url && <a href={a.url} target="_blank" rel="noopener noreferrer" className="dc-linkchip" style={DC_LINKCHIP}>원문 보기 ↗</a>}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </Fragment>
              );
            })}
          </div>

          {/* 핀·감시 관리 — 종목 검색 추가(접이식, 실기능 보존) */}
          <details style={{ margin: '0 14px 16px 14px', border: '1px solid #141B28', borderRadius: 10, background: 'rgba(16,22,31,.4)' }}>
            <summary style={{ padding: '10px 12px', fontSize: 12, color: '#9DA9BE', cursor: 'pointer', userSelect: 'none' }}>📌 내 핀 · 👁 감시 관리 <span style={{ color: '#525E75', fontSize: 10 }}>— 종목 검색해 추가</span></summary>
            <div className="space-y-2" style={{ padding: '0 12px 12px' }}>
              <StockSearch pinned={pinned} onPin={pinTicker} onWatch={(t, n) => toggleWatchStock({ ticker: t, name: n })} onOpen={() => { /* 상세는 관심종목 탭 */ }} />
              {watchWarn && <p className="text-[11.5px]" style={{ color: '#E8938A' }}>{watchWarn}</p>}
              {(pinned.length > 0) && (
                <div className="space-y-1.5">
                  <p className="text-[10.5px] font-semibold" style={{ color: '#6E7B90' }}>📌 핀(보유·관찰) — 손절/목표 도달 시 알림</p>
                  {pinnedRows.map((it) => (
                    <div key={it.ticker} style={{ border: '1px solid #141B28', borderRadius: 8, background: 'rgba(16,22,31,.5)', padding: 8 }}>
                      <div className="flex items-center gap-2 mb-1">
                        <b className="text-[11.5px]" style={{ color: '#E9EFF8' }}>{pinNames[it.ticker] || it.name}</b>
                        <button onClick={() => togglePin(it)} className="ml-auto text-[10.5px]" style={{ color: '#525E75' }}>핀 해제</button>
                      </div>
                      {pinPlans[it.ticker] && <PinnedPlanEditor it={it} plan={pinPlans[it.ticker]} onChange={(p) => updatePlan(it.ticker, p)} />}
                    </div>
                  ))}
                  {pinned.filter((t) => !pinnedRows.some((r) => r.ticker === t)).map((t) => (
                    <div key={t} className="flex items-center gap-2 text-[11.5px] px-1" style={{ color: '#8E9AB0' }}>
                      <span>📌 {pinNames[t] || t}</span><span style={{ color: '#525E75' }}>시세 대기…</span>
                      <button onClick={() => pinTicker(t, pinNames[t] || t)} className="ml-auto text-[10.5px]" style={{ color: '#525E75' }}>해제</button>
                    </div>
                  ))}
                </div>
              )}
              {watch.length > 0 && (() => {
                const wnames = lsGet<Record<string, string>>('dt_watch_names', {});
                return (
                  <div className="space-y-1">
                    <p className="text-[10.5px] font-semibold" style={{ color: '#6E7B90' }}>👁 감시 — 이 종목 소식은 종류 무관 알림</p>
                    {watch.map((tk) => {
                      const nm = wnames[tk] || allItems.find((i) => i.ticker === tk)?.name || tk;
                      return (
                        <div key={tk} className="flex items-center gap-2 text-[11.5px] px-1" style={{ color: '#9DA9BE' }}>
                          <span>👁 {nm}</span>
                          <button onClick={() => toggleWatchStock({ ticker: tk, name: nm })} className="ml-auto text-[10.5px]" style={{ color: '#525E75' }}>해제</button>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              {pinned.length === 0 && watch.length === 0 && <p className="text-[11.5px]" style={{ color: '#525E75' }}>아직 핀·감시 종목이 없어요. 위에서 종목을 검색해 📌 핀(손절/목표) 또는 👁 감시로 추가하세요.</p>}
            </div>
          </details>

          <p style={{ textAlign: 'center', fontSize: 10, color: '#3E4A5F', padding: '0 14px 20px' }}>종목 발굴·차트·상세는 <b style={{ color: '#525E75' }}>전체 스캔</b>·<b style={{ color: '#525E75' }}>관심종목</b> 탭에서 보세요.</p>
        </div>

        {/* ▶ 우열 — 텍스트 레일 (기준 시세 · 오늘의 시간표 · 지금 뉴스) */}
        <div className="dc-col dc-col-right dc-pad dc-scroll">
          <div style={DC_SECHEAD}>{DcIcon.chart(13)}<span style={DC_SECLABEL}>기준 시세</span></div>
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 650, color: '#E9EFF8' }}>SK하이닉스</span>
            <span className="dc-mono" style={{ fontSize: 14, fontWeight: 700, color: hyxp?.pct == null ? '#5E6B82' : hyxp.pct >= 0 ? '#E06C5B' : '#5C8FD6' }}>{hyxp?.pct != null ? `${hyxp.pct >= 0 ? '+' : ''}${hyxp.pct.toFixed(2)}%` : '—'}</span>
          </div>
          <div style={{ marginTop: 7, fontSize: 25, fontWeight: 760, letterSpacing: '-.015em', lineHeight: 1, color: hyxPriceColor }}>
            {hyxp?.price != null ? <>{hyxp.price.toLocaleString('ko-KR')}<span style={{ fontSize: 14, fontWeight: 600, color: hyxPriceColor, opacity: .85 }}>원</span></> : '—'}
          </div>
          {sessTag && hyxp?.price != null && (
            <div style={{ marginTop: 9, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
              title="한국거래소는 2026-09-14부터 16~20시 저녁장(주식만)을 열어요. 공식 종가는 그대로 15:30 정규장 가격이에요.">
              <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 8px', borderRadius: 6, fontSize: 11.5, fontWeight: 550, whiteSpace: 'nowrap',
                border: `1px solid ${sessTag.amber ? 'rgba(220,168,94,.35)' : '#1F2938'}`,
                background: sessTag.amber ? 'rgba(220,168,94,.1)' : 'rgba(19,26,40,.6)',
                color: sessTag.amber ? '#DCA85E' : '#8D99AE' }}>{sessTag.chip}</span>
              <span style={{ fontSize: 11.5, fontWeight: 500, color: '#5E6B82', lineHeight: 1.4 }}>{sessTag.note}</span>
            </div>
          )}
          <div style={{ height: 38, marginTop: 11 }}><TrendSpark pts={series?.hynix} dir={hyxp?.pct ?? null} height={38} fill dot /></div>
          <div style={{ marginTop: 11, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 12.5, fontWeight: 550, color: '#8E9AB0' }}>코스피{sessTag && <span style={{ marginLeft: 6, fontSize: 11.5, fontWeight: 500, color: '#5E6B82' }}>15:30 마감</span>}</span>
            <span className="dc-mono" style={{ fontSize: 14, fontWeight: 700, color: backdrop?.kospi_pct == null ? '#5E6B82' : backdrop.kospi_pct >= 0 ? '#E06C5B' : '#5C8FD6' }}>{backdrop?.kospi_pct != null ? `${backdrop.kospi_pct >= 0 ? '+' : ''}${backdrop.kospi_pct.toFixed(2)}%` : '—'}</span>
          </div>

          <div style={DC_HAIR} />
          <div style={DC_SECHEAD}>{DcIcon.calendar(13)}<span style={DC_SECLABEL}>오늘의 시간표</span></div>
          {news?.calendar && news.calendar.length > 0 ? news.calendar.map((c, i) => {
            const dayWord = c.d_day === 0 ? '오늘' : c.d_day === 1 ? '내일' : c.d_day === 2 ? '모레' : `D-${c.d_day}`;
            return (
              <div key={i} style={{ lineHeight: 1.4, marginTop: i === 0 ? 10 : 8 }}>
                <span style={{ fontSize: 12.5, color: c.risk ? '#E8BC78' : '#8E9AB0' }}>{c.risk ? '⚠️ ' : '🗓️ '}{dayWord} · {c.name}</span>
                {c.note && <span style={{ display: 'block', fontSize: 10, color: '#525E75', lineHeight: 1.3, marginTop: 2 }}>{c.note}</span>}
              </div>
            );
          }) : <div style={{ marginTop: 10, fontSize: 12.5, color: '#525E75', lineHeight: 1.5 }}>예정된 주요 일정이 없어요<span style={{ color: '#414D63' }}>(오늘·내일·모레)</span>.</div>}

          <div style={DC_HAIR} />
          <div style={DC_SECHEAD}>{DcIcon.news(13)}<span style={DC_SECLABEL}>지금 뉴스</span></div>
          <div style={{ marginTop: 4 }}>
            {news?.news && news.news.length > 0 ? news.news.map((it, i) => (
              <a key={i} href={it.url || undefined} target="_blank" rel="noopener noreferrer" className="dc-newslink"
                style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: i === news.news.length - 1 ? 'none' : '1px solid rgba(21,29,43,.6)', cursor: 'pointer' }}>
                <span className="dc-mono" style={{ fontSize: 10.5, color: '#3E4A5F', paddingTop: 2, flex: 'none' }}>{String(i + 1).padStart(2, '0')}</span>
                <div style={{ minWidth: 0 }}>
                  <div className="dc-newstitle" style={{ fontSize: 12.5, fontWeight: 500, color: '#A8B3C6', lineHeight: 1.45 }}>{it.alerted && <span title="신호 영향으로 알림된 기사">🔔 </span>}{it.title}</div>
                  <div style={{ marginTop: 3, fontSize: 10.5, color: '#525E75', display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    {it.source && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.source}</span>}
                    {it.hm && <span style={{ flex: 'none' }}><span style={{ margin: '0 5px', color: '#2C3749' }}>·</span>{it.hm}</span>}
                  </div>
                </div>
              </a>
            )) : <p style={{ marginTop: 8, fontSize: 11, color: '#525E75' }}>시황 뉴스를 불러오는 중…</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
